﻿import React from 'react'
import { Button, Heading, Text, LoadingSpinner, Input, Tooltip } from '../../components/ui'
import { SortableTableHeader } from '../../components/ui/SortableTableHeader'
import { PermissionGuard } from '../../components/PermissionGuard'
import { usePermission } from '../../hooks/usePermissions'
import { modulesService } from '../../services/modules'
import { admDespesasService } from '../../services/admDespesas'
import { admCadastrosService } from '../../services/admCadastros'
import { logDelete, logUpdate, logUserAction } from '../../services/userLogs'
import { printService } from '../../services/printService'
import { useAuthContext } from '../../contexts/AuthContext'
import { supabase } from '../../lib/supabase'
import type {
  AdmOrderHeader,
  AdmOrderItem,
  AdmContract,
  AdmContractService,
  AdmNfeHeader,
  AdmNfeItem,
  AdmNfseHeader,
  AdmNfseItem,
  AdmWithoutNfInsert,
  AdmWithoutNfRow,
  UnifiedDocument,
} from '../../types/admDespesas'
import AdmDespesasBudgetsTab from './AdmDespesasBudgetsTab'
import AdmDespesasContractsTab from './AdmDespesasContractsTab'
import AdmDespesasAllDocumentsTab from './AdmDespesasAllDocumentsTab'
import {
  getExcelSheetNamesFromBuffer,
  parsePlanilhaPagamentosFile,
  type ParsedPlanilhaLinha,
} from '../../utils/admPaymentSpreadsheetImport'
import {
  hasAdmNotaDownloadKey,
  runAdmStorageSignedDownload,
} from '../../utils/admDespesasStorageDownload'
import { matchesPaymentDatePeriod } from '../../utils/admPaymentDateFilter'
import { PaymentDatePeriodFilterFields } from '../../components/adm/PaymentDatePeriodFilterFields'
import AdmDespesasContractEditModal, {
  contractEditPatchFromModal,
  contractEditStateFromRecord,
  type ContractEditModalState,
} from '../../components/adm/AdmDespesasContractEditModal'
import AdmDespesasBudgetEditModal, {
  budgetEditPatchFromModal,
  budgetEditStateFromRecord,
  type BudgetEditModalState,
} from '../../components/adm/AdmDespesasBudgetEditModal'
import type { AdmCategoria, AdmCentroCusto } from '../../types/admCadastros'
import type { PrintConfig, PrintColumn, PrintRow } from '../../types/print'
import { TrashIcon, DocumentIcon, PlusIcon, ArrowUpTrayIcon, XMarkIcon, CheckCircleIcon, PencilIcon, ArrowDownTrayIcon, InformationCircleIcon } from '@heroicons/react/24/outline'

/** Cadastrar Despesas: NF-e/NFS-e, contratos, orçamentos/pedidos e planilha. */
const CADASTRAR_DESPESAS_WEBHOOK_URL =
  'https://webhook.pasiai.com/webhook/Adm-LerDespesas'

/** Texto exibido no filtro e na impressão para a coluna `file_name` dos cabeçalhos. */
const FILTRO_NOME_ARQUIVO = 'Nome do arquivo'
const FILTRO_NOME_ARQUIVO_TITLE = 'Filtra pelo texto da coluna file_name no cabeçalho.'

/** Filtro textual sobre a coluna `file_name` (adm_nfe_header / adm_nfse_header). */
function fileNameQueryMatches(
  fileNameColumn: string | null | undefined,
  query: string
): boolean {
  const q = query.trim().toLowerCase()
  if (!q) return true
  return (fileNameColumn ?? '').toLowerCase().includes(q)
}

type ActiveTab = 'all_documents' | 'nfe' | 'nfse' | 'sem_nf' | 'contracts' | 'budgets'
type CadastroSource = 'notas_fiscais' | 'planilha_pagamentos' | 'contratos' | 'orcamentos_pedidos'

function isCadastroUploadContratosTipo(s: CadastroSource): boolean {
  return s === 'contratos' || s === 'orcamentos_pedidos'
}

/** Valor do campo `tipo` no webhook; omisso em NF-e/NFS-e e planilha. */
function getTipoParamForCadastro(fluxoCadastro: CadastroSource): string | null {
  if (fluxoCadastro === 'contratos') return 'CONTRATO'
  if (fluxoCadastro === 'orcamentos_pedidos') return 'ORCAMENTO/PEDIDO'
  return null
}

async function hasActiveRowByFileName(
  table: 'adm_nfe_header' | 'adm_nfse_header' | 'adm_contracts' | 'adm_order_header',
  fileName: string
): Promise<boolean> {
  const { data, error } = await supabase
    .from(table)
    .select('id')
    .eq('file_name', fileName)
    .is('deleted_at', null)
    .limit(1)

  if (error) throw error
  return (data?.length ?? 0) > 0
}

async function isArquivoJaProcessado(fluxoCadastro: CadastroSource, fileName: string): Promise<boolean> {
  const normalized = fileName.trim()
  if (!normalized) return false
  if (fluxoCadastro === 'notas_fiscais') {
    const [existsNfe, existsNfse] = await Promise.all([
      hasActiveRowByFileName('adm_nfe_header', normalized),
      hasActiveRowByFileName('adm_nfse_header', normalized),
    ])
    return existsNfe || existsNfse
  }
  if (fluxoCadastro === 'contratos') {
    return hasActiveRowByFileName('adm_contracts', normalized)
  }
  if (fluxoCadastro === 'orcamentos_pedidos') {
    return hasActiveRowByFileName('adm_order_header', normalized)
  }
  return false
}

function isPdfFileForUpload(file: File): boolean {
  const n = file.name.toLowerCase()
  return file.type === 'application/pdf' || n.endsWith('.pdf')
}

function isImageFileForUpload(file: File): boolean {
  if (file.type && file.type.startsWith('image/')) return true
  const n = file.name.toLowerCase()
  return /\.(jpe?g|png|gif|webp|bmp|tif|tiff|heic|heif)$/i.test(n)
}

function isPdfOrImageFileForContratos(file: File): boolean {
  return isPdfFileForUpload(file) || isImageFileForUpload(file)
}
type ActiveSubTab = 'notes' | 'items'
type PrintScope = 'page' | 'filtered'
type PrintViewKey = 'nfe_notes' | 'nfe_items' | 'nfse_notes' | 'nfse_items'
type HeaderEditType = 'nfe' | 'nfse'
type HeaderEditModalState = {
  type: HeaderEditType
  id: string
  number: string
  series: string
  rpsNumber: string
  name: string
  document: string
  providerIm: string
  municipality: string
  recipientName: string
  recipientDocument: string
  competenceDate: string
  accessKey: string
  issueDate: string
  totalProducts: string
  totalServices: string
  totalFreight: string
  totalDiscount: string
  netValue: string
  totalValue: string
  paymentMethod: string
  paymentDate: string
  manualNotes: string
  fileName: string
  fileNameStorage: string
}

type ItemEditModalState = {
  tableType: 'nfe' | 'nfse'
  itemId: string
  headerId: string
  headerNumber: string
  description: string
  ncm: string
  unit: string
  serviceCode: string
  quantity: string
  unitValue: string
  totalValue: string
  categoryId: string
  costCenterId: string
}

type WithoutNfEditModalState = {
  id: string
  paymentType: string
  expenseDate: string
  description: string
  installment: string
  amountPaid: string
  amountToPay: string
  notes: string
  paymentDate: string
  manualNotes: string
  categoryId: string
  costCenterId: string
}

function formatCurrency(value: number | null): string {
  if (value == null) return '—'
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

function formatQuantity(value: number): string {
  return value.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 6 })
}

function formatDate(value: string | null): string {
  if (!value) return '—'
  return new Date(value).toLocaleDateString('pt-BR')
}

function getNfseDisplayTotal(nfse: AdmNfseHeader): number | null {
  return nfse.net_value ?? nfse.total_services ?? null
}

function formatTime(date: Date): string {
  return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
}

function formatDocument(value: string | null): string {
  if (!value) return '—'
  const digits = value.replace(/\D/g, '')
  if (digits.length === 11) {
    return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
  }
  if (digits.length === 14) {
    return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')
  }
  return value
}

const InfoTip: React.FC<{ content: React.ReactNode; position?: 'top' | 'bottom' | 'left' | 'right' }> = ({ content, position = 'bottom' }) => (
  <Tooltip content={content} position={position} constrainWidth className="inline-flex shrink-0 align-middle">
    <span className="inline-flex rounded-full p-0.5 text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200">
      <InformationCircleIcon className="h-4 w-4" />
    </span>
  </Tooltip>
)

const AdmDespesas: React.FC = () => {
  const { user } = useAuthContext()
  const [activeTab, setActiveTab] = React.useState<ActiveTab>('all_documents')
  const [nfeSubTab, setNfeSubTab] = React.useState<ActiveSubTab>('notes')
  const [nfseSubTab, setNfseSubTab] = React.useState<ActiveSubTab>('notes')
  const [loading, setLoading] = React.useState(true)
  const [pageError, setPageError] = React.useState<string | null>(null)
  const [notaDocDownloadBusy, setNotaDocDownloadBusy] = React.useState(false)
  const [headerEditFileDownloadError, setHeaderEditFileDownloadError] = React.useState<string | null>(null)

  const [nfeList, setNfeList] = React.useState<AdmNfeHeader[]>([])
  const [nfseList, setNfseList] = React.useState<AdmNfseHeader[]>([])
  const [allNfeItems, setAllNfeItems] = React.useState<AdmNfeItem[]>([])
  const [allNfseItems, setAllNfseItems] = React.useState<AdmNfseItem[]>([])
  const [contractsList, setContractsList] = React.useState<AdmContract[]>([])
  const [allContractServices, setAllContractServices] = React.useState<AdmContractService[]>([])
  const [budgetList, setBudgetList] = React.useState<AdmOrderHeader[]>([])
  const [allBudgetItems, setAllBudgetItems] = React.useState<AdmOrderItem[]>([])

  const [categoryMap, setCategoryMap] = React.useState<Map<string, string>>(new Map())
  const [costCenterMap, setCostCenterMap] = React.useState<Map<string, string>>(new Map())
  const [categorias, setCategorias] = React.useState<AdmCategoria[]>([])
  const [centrosCusto, setCentrosCusto] = React.useState<AdmCentroCusto[]>([])

  const PAGE_SIZE = 20
  // Paginação
  const [nfeNotesPage, setNfeNotesPage] = React.useState(1)
  const [nfeItemsPage, setNfeItemsPage] = React.useState(1)
  const [nfseNotesPage, setNfseNotesPage] = React.useState(1)
  const [nfseItemsPage, setNfseItemsPage] = React.useState(1)

  // Ordenação
  const [nfeNotesSort, setNfeNotesSort] = React.useState('issue_date')
  const [nfeNotesSortOrder, setNfeNotesSortOrder] = React.useState<'asc' | 'desc'>('desc')
  const [nfseNotesSort, setNfseNotesSort] = React.useState('issue_date')
  const [nfseNotesSortOrder, setNfseNotesSortOrder] = React.useState<'asc' | 'desc'>('desc')
  const [nfeItemsSort, setNfeItemsSort] = React.useState('nfe_id')
  const [nfeItemsSortOrder, setNfeItemsSortOrder] = React.useState<'asc' | 'desc'>('asc')
  const [nfseItemsSort, setNfseItemsSort] = React.useState('nfse_id')
  const [nfseItemsSortOrder, setNfseItemsSortOrder] = React.useState<'asc' | 'desc'>('asc')

  const toggleSort = (
    current: string, setSort: (v: string) => void,
    currentOrder: 'asc' | 'desc', setOrder: (v: 'asc' | 'desc') => void,
    setPage: (v: number) => void,
  ) => (key: string) => {
    if (current === key) {
      setOrder(currentOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSort(key)
      setOrder('asc')
    }
    setPage(1)
  }

  // Filtros - Notas NF-e
  const [nfeNotesSearch, setNfeNotesSearch] = React.useState('')
  const [nfeNotesCnpj, setNfeNotesCnpj] = React.useState('')
  const [nfeNotesNumber, setNfeNotesNumber] = React.useState('')
  const [nfeNotesAccessKey, setNfeNotesAccessKey] = React.useState('')
  const [nfeNotesFileName, setNfeNotesFileName] = React.useState('')
  const [nfePaymentDateFrom, setNfePaymentDateFrom] = React.useState('')
  const [nfePaymentDateTo, setNfePaymentDateTo] = React.useState('')
  // Filtros - Itens NF-e
  const [nfeItemsSearch, setNfeItemsSearch] = React.useState('')
  const [nfeItemsIssuer, setNfeItemsIssuer] = React.useState('')
  const [nfeItemsNoteNumber, setNfeItemsNoteNumber] = React.useState('')
  const [nfeItemsAccessKey, setNfeItemsAccessKey] = React.useState('')
  const [nfeItemsFileName, setNfeItemsFileName] = React.useState('')
  const [nfeItemsCategory, setNfeItemsCategory] = React.useState('')
  const [nfeItemsCostCenter, setNfeItemsCostCenter] = React.useState('')
  // Filtros - Notas NFS-e
  const [nfseNotesSearch, setNfseNotesSearch] = React.useState('')
  const [nfseNotesCnpj, setNfseNotesCnpj] = React.useState('')
  const [nfseNotesNumber, setNfseNotesNumber] = React.useState('')
  const [nfseNotesAccessKey, setNfseNotesAccessKey] = React.useState('')
  const [nfseNotesFileName, setNfseNotesFileName] = React.useState('')
  const [nfsePaymentDateFrom, setNfsePaymentDateFrom] = React.useState('')
  const [nfsePaymentDateTo, setNfsePaymentDateTo] = React.useState('')
  // Filtros - Itens NFS-e
  const [nfseItemsSearch, setNfseItemsSearch] = React.useState('')
  const [nfseItemsProvider, setNfseItemsProvider] = React.useState('')
  const [nfseItemsNoteNumber, setNfseItemsNoteNumber] = React.useState('')
  const [nfseItemsAccessKey, setNfseItemsAccessKey] = React.useState('')
  const [nfseItemsFileName, setNfseItemsFileName] = React.useState('')
  const [nfseItemsServiceCode, setNfseItemsServiceCode] = React.useState('')
  const [nfseItemsCategory, setNfseItemsCategory] = React.useState('')
  const [nfseItemsCostCenter, setNfseItemsCostCenter] = React.useState('')

  const [withoutNfList, setWithoutNfList] = React.useState<AdmWithoutNfRow[]>([])
  const [semNfPage, setSemNfPage] = React.useState(1)
  const [semNfSort, setSemNfSort] = React.useState('expense_date')
  const [semNfSortOrder, setSemNfSortOrder] = React.useState<'asc' | 'desc'>('desc')
  const [semNfDesc, setSemNfDesc] = React.useState('')
  const [semNfPaymentType, setSemNfPaymentType] = React.useState('')
  const [semNfCategory, setSemNfCategory] = React.useState('')
  const [semNfCostCenter, setSemNfCostCenter] = React.useState('')
  const [semNfDateFrom, setSemNfDateFrom] = React.useState('')
  const [semNfDateTo, setSemNfDateTo] = React.useState('')
  const [semNfPaymentDateFrom, setSemNfPaymentDateFrom] = React.useState('')
  const [semNfPaymentDateTo, setSemNfPaymentDateTo] = React.useState('')

  const [savingItem, setSavingItem] = React.useState<string | null>(null)

  const [moduleInfo, setModuleInfo] = React.useState<{ display_name: string; description: string | null } | null>(null)

  const [deleteModal, setDeleteModal] = React.useState<{
    type: 'nfe' | 'nfse' | 'sem_nf'
    id: string
    label: string
  } | null>(null)
  const [deleteReason, setDeleteReason] = React.useState('')
  const [submittingDelete, setSubmittingDelete] = React.useState(false)
  const [headerEditModal, setHeaderEditModal] = React.useState<HeaderEditModalState | null>(null)
  const [submittingHeaderEdit, setSubmittingHeaderEdit] = React.useState(false)
  const [itemEditModal, setItemEditModal] = React.useState<ItemEditModalState | null>(null)
  const [submittingItemEdit, setSubmittingItemEdit] = React.useState(false)
  const [withoutNfEditModal, setWithoutNfEditModal] = React.useState<WithoutNfEditModalState | null>(null)
  const [submittingWithoutNfEdit, setSubmittingWithoutNfEdit] = React.useState(false)
  const [withoutNfDetailModal, setWithoutNfDetailModal] = React.useState<AdmWithoutNfRow | null>(null)
  const [contractEditModal, setContractEditModal] = React.useState<ContractEditModalState | null>(null)
  const [submittingContractEdit, setSubmittingContractEdit] = React.useState(false)
  const [budgetEditModal, setBudgetEditModal] = React.useState<BudgetEditModalState | null>(null)
  const [submittingBudgetEdit, setSubmittingBudgetEdit] = React.useState(false)
  const [budgetEditDownloadError, setBudgetEditDownloadError] = React.useState<string | null>(null)
  const [budgetEditDownloadBusy, setBudgetEditDownloadBusy] = React.useState(false)
  const [pendingUnifiedDetail, setPendingUnifiedDetail] = React.useState<{
    source: 'contracts' | 'budgets'
    id: string
  } | null>(null)
  const [documentsRefreshKey, setDocumentsRefreshKey] = React.useState(0)

  type FileStatus = { file: File; status: 'pending' | 'sending' | 'success' | 'error'; error?: string; finishedAt?: Date }

  const [detailNfe, setDetailNfe] = React.useState<{ header: AdmNfeHeader; items: AdmNfeItem[] } | null>(null)
  const [detailNfse, setDetailNfse] = React.useState<{ header: AdmNfseHeader; items: AdmNfseItem[] } | null>(null)
  const [loadingDetail, setLoadingDetail] = React.useState(false)

  const [uploadModalOpen, setUploadModalOpen] = React.useState(false)
  const [uploadFiles, setUploadFiles] = React.useState<FileStatus[]>([])
  const [isProcessing, setIsProcessing] = React.useState(false)
  const [processingDone, setProcessingDone] = React.useState(false)
  const [processingStartedAt, setProcessingStartedAt] = React.useState<Date | null>(null)
  const [processingFinishedAt, setProcessingFinishedAt] = React.useState<Date | null>(null)
  const [cadastroSource, setCadastroSource] = React.useState<CadastroSource>('notas_fiscais')
  const [excelFileName, setExcelFileName] = React.useState<string | null>(null)
  const [excelArrayBuffer, setExcelArrayBuffer] = React.useState<ArrayBuffer | null>(null)
  const [excelSheetNames, setExcelSheetNames] = React.useState<string[]>([])
  const [excelSelectedSheet, setExcelSelectedSheet] = React.useState('')
  const [excelHeaderRow, setExcelHeaderRow] = React.useState('1')
  const [excelFirstDataRow, setExcelFirstDataRow] = React.useState('2')
  const [excelParsedRows, setExcelParsedRows] = React.useState<ParsedPlanilhaLinha[] | null>(null)
  const [excelParseError, setExcelParseError] = React.useState<string | null>(null)
  const [spreadsheetSubmitting, setSpreadsheetSubmitting] = React.useState(false)
  const [spreadsheetImportDone, setSpreadsheetImportDone] = React.useState(false)
  const fileInputRef = React.useRef<HTMLInputElement>(null)
  const excelInputRef = React.useRef<HTMLInputElement>(null)
  const [printDialogView, setPrintDialogView] = React.useState<PrintViewKey | null>(null)

  const { hasPermission: canDelete } = usePermission('adm_despesas', 'delete')
  const { hasPermission: canEdit } = usePermission('adm_despesas', 'update')

  const getErrorMessage = (error: unknown): string => {
    if (error instanceof Error) return error.message
    const maybe = error as { message?: string; code?: string }
    if (maybe?.message) return maybe.message
    if (maybe?.code) return `Erro (${maybe.code})`
    return 'Erro inesperado ao processar solicitação.'
  }

  const bumpDocumentsRefreshKey = React.useCallback(() => {
    setDocumentsRefreshKey((k) => k + 1)
  }, [])

  const ADM_NOTA_SIGNED_URL_TTL = 600

  const runAdmNotaSignedDownloadCore = React.useCallback(
    async (
      fileNameStorage: string | null | undefined,
      fileName: string | null | undefined
    ): Promise<{ ok: true } | { ok: false; message: string }> =>
      runAdmStorageSignedDownload(supabase, {
        fileNameStorage,
        fileName,
        storageFolderPrefix: 'notas',
        signedUrlTtlSec: ADM_NOTA_SIGNED_URL_TTL,
      }),
    []
  )

  const openAdmNotaSignedDownload = React.useCallback(
    async (fileNameStorage: string | null | undefined, fileName: string | null | undefined) => {
      if (!hasAdmNotaDownloadKey(fileNameStorage, fileName)) return
      setNotaDocDownloadBusy(true)
      setPageError(null)
      try {
        const r = await runAdmNotaSignedDownloadCore(fileNameStorage, fileName)
        if (!r.ok) setPageError(r.message)
      } finally {
        setNotaDocDownloadBusy(false)
      }
    },
    [runAdmNotaSignedDownloadCore]
  )

  const openAdmNotaSignedDownloadFromHeaderEdit = React.useCallback(async () => {
    if (!headerEditModal) return
    if (!hasAdmNotaDownloadKey(headerEditModal.fileNameStorage, headerEditModal.fileName)) {
      setHeaderEditFileDownloadError('Informe file_name ou file_name_storage para baixar o documento.')
      return
    }
    setNotaDocDownloadBusy(true)
    setHeaderEditFileDownloadError(null)
    setPageError(null)
    try {
      const r = await runAdmNotaSignedDownloadCore(
        headerEditModal.fileNameStorage,
        headerEditModal.fileName
      )
      if (!r.ok) setHeaderEditFileDownloadError(r.message)
    } finally {
      setNotaDocDownloadBusy(false)
    }
  }, [headerEditModal, runAdmNotaSignedDownloadCore])

  const loadData = React.useCallback(async () => {
    setLoading(true)
    setPageError(null)
    try {
      const [nfe, nfse, nfeItems, nfseItems, withoutNf, contracts, contractServices, cats, centros] = await Promise.all([
        admDespesasService.listNfe(),
        admDespesasService.listNfse(),
        admDespesasService.listAllNfeItems(),
        admDespesasService.listAllNfseItems(),
        admDespesasService.listWithoutNf(),
        admDespesasService.listContracts(),
        admDespesasService.listAllContractServices(),
        admCadastrosService.listCategorias(),
        admCadastrosService.listCentrosCusto(),
      ])
      let budgets: AdmOrderHeader[] = []
      let budgetItemsAll: AdmOrderItem[] = []
      try {
        ;[budgets, budgetItemsAll] = await Promise.all([
          admDespesasService.listOrderHeaders(),
          admDespesasService.listAllOrderItems(),
        ])
      } catch {
        /* Tabelas adm_order_* ausentes ou erro transitório: abas Contratos/NF continuam utilizáveis. */
      }
      setNfeList(nfe)
      setNfseList(nfse)
      setAllNfeItems(nfeItems)
      setAllNfseItems(nfseItems)
      setWithoutNfList(withoutNf)
      setContractsList(contracts)
      setAllContractServices(contractServices)
      setBudgetList(budgets)
      setAllBudgetItems(budgetItemsAll)
      setCategoryMap(new Map(cats.map((c) => [c.id, c.name])))
      setCostCenterMap(new Map(centros.map((c) => [c.id, c.name])))
      setCategorias(cats)
      setCentrosCusto(centros)
    } catch (error) {
      setPageError(getErrorMessage(error))
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => {
    void loadData()
  }, [loadData])

  const clearPendingUnifiedDetail = React.useCallback(() => {
    setPendingUnifiedDetail(null)
  }, [])

  const refreshDespesasData = React.useCallback(() => {
    void loadData()
    bumpDocumentsRefreshKey()
  }, [bumpDocumentsRefreshKey, loadData])

  React.useEffect(() => {
    const loadModule = async () => {
      const result = await modulesService.getByName('adm_despesas')
      if (result.success && result.data) {
        setModuleInfo({
          display_name: result.data.display_name || 'ADM • Despesas',
          description: result.data.description || null,
        })
      }
    }
    void loadModule()
  }, [])

  const summarizeFilters = (entries: Array<[string, string]>): Array<{ label: string; value: string }> => {
    return entries
      .map(([label, value]) => [label, value.trim()] as const)
      .filter(([, value]) => value.length > 0)
      .map(([label, value]) => ({ label, value }))
  }

  const getPrintPayload = (view: PrintViewKey, scope: PrintScope): PrintConfig => {
    const generatedAt = new Date().toLocaleString('pt-BR')
    const scopeLabel = scope === 'page' ? 'Página atual' : 'Todos os filtrados'

    if (view === 'nfe_notes') {
      const source = scope === 'page' ? paginatedNfeNotes : sortedNfeNotes
      const columns: PrintColumn[] = [
        { key: 'number', label: 'Número' },
        { key: 'issuer_name', label: 'Emitente' },
        { key: 'issue_date', label: 'Emissão' },
        { key: 'items_count', label: 'Nº Itens', align: 'right' },
        { key: 'total_value', label: 'Valor Total', align: 'right' },
      ]
      const rows: PrintRow[] = source.map((nfe) => ({
        number: nfe.number || '—',
        issuer_name: nfe.issuer_name || '—',
        issue_date: formatDate(nfe.issue_date),
        items_count: nfeItemsCountByHeader.get(nfe.id) ?? 0,
        total_value: formatCurrency(nfe.total_value),
      }))
      return {
        reportTitle: 'ADM • Despesas • Compras NF-e • Notas Fiscais',
        generatedAt,
        scopeLabel,
        totalRows: rows.length,
        filters: summarizeFilters([
          ['Descrição', nfeItemsSearch],
          ['Emitente', nfeItemsIssuer],
          ['Número da nota', nfeItemsNoteNumber],
          ['Chave de acesso', nfeItemsAccessKey],
          [FILTRO_NOME_ARQUIVO, nfeItemsFileName],
          ['Categoria', nfeItemsCategory ? (categoryMap.get(nfeItemsCategory) ?? nfeItemsCategory) : ''],
          ['Centro de custo', nfeItemsCostCenter ? (costCenterMap.get(nfeItemsCostCenter) ?? nfeItemsCostCenter) : ''],
        ]),
        columns,
        rows,
      }
    }

    if (view === 'nfe_items') {
      const source = scope === 'page' ? paginatedNfeItems : sortedNfeItems
      const columns: PrintColumn[] = [
        { key: 'note_number', label: 'Nota' },
        { key: 'issuer_name', label: 'Emitente' },
        { key: 'description', label: 'Descrição' },
        { key: 'quantity', label: 'Qtd', align: 'right' },
        { key: 'unit', label: 'Un' },
        { key: 'unit_value', label: 'Vl. Unit.', align: 'right' },
        { key: 'total_value', label: 'Total', align: 'right' },
        { key: 'category', label: 'Categoria' },
        { key: 'cost_center', label: 'Centro de Custo' },
      ]
      const rows: PrintRow[] = source.map((item) => {
        const header = nfeHeaderMap.get(item.nfe_id)
        return {
          note_number: header?.number || '—',
          issuer_name: header?.issuer_name || '—',
          description: item.description || '—',
          quantity: item.quantity ?? '—',
          unit: item.unit || '—',
          unit_value: formatCurrency(item.unit_value),
          total_value: formatCurrency(item.total_value),
          category: item.category_id ? (categoryMap.get(item.category_id) ?? item.category_id) : '—',
          cost_center: item.cost_center_id ? (costCenterMap.get(item.cost_center_id) ?? item.cost_center_id) : '—',
        }
      })
      return {
        reportTitle: 'ADM • Despesas • Compras NF-e • Itens',
        generatedAt,
        scopeLabel,
        totalRows: rows.length,
        filters: summarizeFilters([
          ['Descrição', nfeItemsSearch],
          ['Emitente', nfeItemsIssuer],
          ['Número da nota', nfeItemsNoteNumber],
          ['Chave de acesso', nfeItemsAccessKey],
          [FILTRO_NOME_ARQUIVO, nfeItemsFileName],
          ['Categoria', nfeItemsCategory ? (categoryMap.get(nfeItemsCategory) ?? nfeItemsCategory) : ''],
          ['Centro de custo', nfeItemsCostCenter ? (costCenterMap.get(nfeItemsCostCenter) ?? nfeItemsCostCenter) : ''],
        ]),
        columns,
        rows,
      }
    }

    if (view === 'nfse_notes') {
      const source = scope === 'page' ? paginatedNfseNotes : sortedNfseNotes
      const columns: PrintColumn[] = [
        { key: 'number', label: 'Número' },
        { key: 'provider_name', label: 'Prestador' },
        { key: 'competence', label: 'Competência' },
        { key: 'total_value', label: 'Valor Total', align: 'right' },
      ]
      const rows: PrintRow[] = source.map((nfse) => ({
        number: nfse.number || '—',
        provider_name: nfse.provider_name || '—',
        competence: nfse.competence_date
          ? new Date(`${nfse.competence_date}T00:00:00`).toLocaleDateString('pt-BR', { month: '2-digit', year: 'numeric' })
          : '—',
        total_value: formatCurrency(getNfseDisplayTotal(nfse)),
      }))
      return {
        reportTitle: 'ADM • Despesas • Serviços NFS-e • Notas Fiscais',
        generatedAt,
        scopeLabel,
        totalRows: rows.length,
        filters: summarizeFilters([
          ['Prestador', nfseItemsProvider],
          ['Número da nota', nfseItemsNoteNumber],
          ['Chave de acesso', nfseItemsAccessKey],
          [FILTRO_NOME_ARQUIVO, nfseItemsFileName],
          ['Categoria', nfseItemsCategory ? (categoryMap.get(nfseItemsCategory) ?? nfseItemsCategory) : ''],
          ['Centro de custo', nfseItemsCostCenter ? (costCenterMap.get(nfseItemsCostCenter) ?? nfseItemsCostCenter) : ''],
        ]),
        columns,
        rows,
      }
    }

    const source = scope === 'page' ? paginatedNfseItems : sortedNfseItems
    const columns: PrintColumn[] = [
      { key: 'note_number', label: 'Nota' },
      { key: 'provider_name', label: 'Prestador' },
      { key: 'description', label: 'Detalhes do Serviço' },
      { key: 'quantity', label: 'Qtd', align: 'right' },
      { key: 'unit_value', label: 'Vl. Unit.', align: 'right' },
      { key: 'total_value', label: 'Total', align: 'right' },
      { key: 'category', label: 'Categoria' },
      { key: 'cost_center', label: 'Centro de Custo' },
    ]
    const rows: PrintRow[] = source.map((item) => {
      const header = nfseHeaderMap.get(item.nfse_id)
      return {
        note_number: header?.number || '—',
        provider_name: header?.provider_name || '—',
        description: item.description || '—',
        quantity: item.quantity ?? '—',
        unit_value: formatCurrency(item.unit_value),
        total_value: formatCurrency(item.total_value),
        category: item.category_id ? (categoryMap.get(item.category_id) ?? item.category_id) : '—',
        cost_center: item.cost_center_id ? (costCenterMap.get(item.cost_center_id) ?? item.cost_center_id) : '—',
      }
    })
    return {
      reportTitle: 'ADM • Despesas • Serviços NFS-e • Serviços',
      generatedAt,
      scopeLabel,
      totalRows: rows.length,
      filters: summarizeFilters([
        ['Descrição', nfseItemsSearch],
        ['Prestador', nfseItemsProvider],
        ['Número da nota', nfseItemsNoteNumber],
        ['Chave de acesso', nfseItemsAccessKey],
        [FILTRO_NOME_ARQUIVO, nfseItemsFileName],
        ['Categoria', nfseItemsCategory ? (categoryMap.get(nfseItemsCategory) ?? nfseItemsCategory) : ''],
        ['Centro de custo', nfseItemsCostCenter ? (costCenterMap.get(nfseItemsCostCenter) ?? nfseItemsCostCenter) : ''],
      ]),
      columns,
      rows,
    }
  }

  const openPrintDialog = (view: PrintViewKey) => {
    setPrintDialogView(view)
  }

  const handlePrint = (scope: PrintScope) => {
    if (!printDialogView) return
    const payload = getPrintPayload(printDialogView, scope)
    printService.print(payload)
    setPrintDialogView(null)
  }

  const openDeleteModal = (type: 'nfe' | 'nfse' | 'sem_nf', id: string, label: string) => {
    setDeleteModal({ type, id, label })
    setDeleteReason('')
  }

  const closeDeleteModal = () => {
    setDeleteModal(null)
    setDeleteReason('')
  }

  const openHeaderEditModal = (type: HeaderEditType, header: AdmNfeHeader | AdmNfseHeader) => {
    setHeaderEditFileDownloadError(null)
    if (type === 'nfe') {
      const nfe = header as AdmNfeHeader
      setHeaderEditModal({
        type,
        id: nfe.id,
        number: nfe.number ?? '',
        series: nfe.series ?? '',
        rpsNumber: '',
        name: nfe.issuer_name ?? '',
        document: nfe.issuer_cnpj ?? '',
        providerIm: '',
        municipality: '',
        recipientName: nfe.recipient_name ?? '',
        recipientDocument: nfe.recipient_document ?? '',
        competenceDate: '',
        accessKey: nfe.access_key ?? '',
        issueDate: nfe.issue_date ? nfe.issue_date.slice(0, 10) : '',
        totalProducts: nfe.total_products != null ? String(nfe.total_products) : '',
        totalServices: '',
        totalFreight: nfe.total_freight != null ? String(nfe.total_freight) : '',
        totalDiscount: nfe.total_discount != null ? String(nfe.total_discount) : '',
        netValue: '',
        totalValue: nfe.total_value != null ? String(nfe.total_value) : '',
        paymentMethod: nfe.payment_method ?? '',
        paymentDate: nfe.payment_date ? nfe.payment_date.slice(0, 10) : '',
        manualNotes: nfe.manual_notes ?? '',
        fileName: nfe.file_name ?? '',
        fileNameStorage: nfe.file_name_storage ?? '',
      })
      return
    }

    const nfse = header as AdmNfseHeader
    setHeaderEditModal({
      type,
      id: nfse.id,
      number: nfse.number ?? '',
      series: '',
      rpsNumber: nfse.rps_number ?? '',
      name: nfse.provider_name ?? '',
      document: nfse.provider_cnpj ?? '',
      providerIm: nfse.provider_im ?? '',
      municipality: nfse.municipality ?? '',
      recipientName: '',
      recipientDocument: '',
      competenceDate: nfse.competence_date ? nfse.competence_date.slice(0, 10) : '',
      accessKey: nfse.access_key ?? '',
      issueDate: nfse.issue_date ? nfse.issue_date.slice(0, 10) : '',
      totalProducts: '',
      totalServices: nfse.total_services != null ? String(nfse.total_services) : '',
      totalFreight: '',
      totalDiscount: '',
      netValue: nfse.net_value != null ? String(nfse.net_value) : '',
      totalValue: '',
      paymentMethod: nfse.payment_method ?? '',
      paymentDate: nfse.payment_date ? nfse.payment_date.slice(0, 10) : '',
      manualNotes: nfse.manual_notes ?? '',
      fileName: nfse.file_name ?? '',
      fileNameStorage: nfse.file_name_storage ?? '',
    })
  }

  const closeHeaderEditModal = () => {
    if (submittingHeaderEdit) return
    setHeaderEditFileDownloadError(null)
    setHeaderEditModal(null)
  }

  const handleSaveHeaderEdit = async () => {
    if (!headerEditModal) return
    setSubmittingHeaderEdit(true)
    try {
      if (headerEditModal.type === 'nfe') {
        const currentHeader = nfeList.find((item) => item.id === headerEditModal.id)
        const patch = {
          number: headerEditModal.number.trim() || null,
          series: headerEditModal.series.trim() || null,
          issuer_name: headerEditModal.name.trim() || null,
          issuer_cnpj: headerEditModal.document.trim() || null,
          recipient_name: headerEditModal.recipientName.trim() || null,
          recipient_document: headerEditModal.recipientDocument.trim() || null,
          access_key: headerEditModal.accessKey.trim() || currentHeader?.access_key || '',
          issue_date: headerEditModal.issueDate || null,
          total_products: headerEditModal.totalProducts.trim() ? Number(headerEditModal.totalProducts) : null,
          total_freight: headerEditModal.totalFreight.trim() ? Number(headerEditModal.totalFreight) : null,
          total_discount: headerEditModal.totalDiscount.trim() ? Number(headerEditModal.totalDiscount) : null,
          total_value: headerEditModal.totalValue.trim() ? Number(headerEditModal.totalValue) : null,
          payment_method: headerEditModal.paymentMethod.trim() || null,
          payment_date: headerEditModal.paymentDate.trim() || null,
          manual_notes: headerEditModal.manualNotes.trim() || null,
          file_name: currentHeader?.file_name ?? (headerEditModal.fileName.trim() || null),
        }
        await admDespesasService.updateNfeHeader(headerEditModal.id, patch)
        setNfeList((prev) => prev.map((item) => (item.id === headerEditModal.id ? { ...item, ...patch } : item)))
        setDetailNfe((prev) => prev && prev.header.id === headerEditModal.id ? { ...prev, header: { ...prev.header, ...patch } } : prev)
        await logUpdate(
          'other',
          headerEditModal.id,
          `Atualizou cabeçalho da NF-e ${patch.number ?? currentHeader?.number ?? headerEditModal.id}`,
          {
            number: currentHeader?.number ?? null,
            series: currentHeader?.series ?? null,
            issuer_name: currentHeader?.issuer_name ?? null,
            issuer_cnpj: currentHeader?.issuer_cnpj ?? null,
            recipient_name: currentHeader?.recipient_name ?? null,
            recipient_document: currentHeader?.recipient_document ?? null,
            access_key: currentHeader?.access_key ?? null,
            issue_date: currentHeader?.issue_date ?? null,
            total_products: currentHeader?.total_products ?? null,
            total_freight: currentHeader?.total_freight ?? null,
            total_discount: currentHeader?.total_discount ?? null,
            total_value: currentHeader?.total_value ?? null,
            payment_method: currentHeader?.payment_method ?? null,
            payment_date: currentHeader?.payment_date ?? null,
            manual_notes: currentHeader?.manual_notes ?? null,
            file_name: currentHeader?.file_name ?? null,
          },
          patch,
          { additional_info: 'Módulo ADM • Despesas (cabeçalho NF-e)' }
        )
      } else {
        const currentHeader = nfseList.find((item) => item.id === headerEditModal.id)
        const patch = {
          number: headerEditModal.number.trim() || null,
          rps_number: headerEditModal.rpsNumber.trim() || null,
          provider_name: headerEditModal.name.trim() || null,
          provider_cnpj: headerEditModal.document.trim() || null,
          provider_im: headerEditModal.providerIm.trim() || null,
          municipality: headerEditModal.municipality.trim() || null,
          customer_name: headerEditModal.recipientName.trim() || null,
          customer_document: headerEditModal.recipientDocument.trim() || null,
          access_key: headerEditModal.accessKey.trim() || currentHeader?.access_key || '',
          issue_date: headerEditModal.issueDate || null,
          competence_date: headerEditModal.competenceDate || null,
          total_services: headerEditModal.totalServices.trim() ? Number(headerEditModal.totalServices) : null,
          total_discount: headerEditModal.totalDiscount.trim() ? Number(headerEditModal.totalDiscount) : null,
          net_value: headerEditModal.netValue.trim() ? Number(headerEditModal.netValue) : null,
          payment_method: headerEditModal.paymentMethod.trim() || null,
          payment_date: headerEditModal.paymentDate.trim() || null,
          manual_notes: headerEditModal.manualNotes.trim() || null,
          file_name: currentHeader?.file_name ?? (headerEditModal.fileName.trim() || null),
        }
        await admDespesasService.updateNfseHeader(headerEditModal.id, patch)
        setNfseList((prev) => prev.map((item) => (item.id === headerEditModal.id ? { ...item, ...patch } : item)))
        setDetailNfse((prev) => prev && prev.header.id === headerEditModal.id ? { ...prev, header: { ...prev.header, ...patch } } : prev)
        await logUpdate(
          'other',
          headerEditModal.id,
          `Atualizou cabeçalho da NFS-e ${patch.number ?? currentHeader?.number ?? headerEditModal.id}`,
          {
            number: currentHeader?.number ?? null,
            rps_number: currentHeader?.rps_number ?? null,
            provider_name: currentHeader?.provider_name ?? null,
            provider_cnpj: currentHeader?.provider_cnpj ?? null,
            provider_im: currentHeader?.provider_im ?? null,
            municipality: currentHeader?.municipality ?? null,
            customer_name: currentHeader?.customer_name ?? null,
            customer_document: currentHeader?.customer_document ?? null,
            access_key: currentHeader?.access_key ?? null,
            issue_date: currentHeader?.issue_date ?? null,
            competence_date: currentHeader?.competence_date ?? null,
            total_services: currentHeader?.total_services ?? null,
            total_discount: currentHeader?.total_discount ?? null,
            net_value: currentHeader?.net_value ?? null,
            payment_method: currentHeader?.payment_method ?? null,
            payment_date: currentHeader?.payment_date ?? null,
            manual_notes: currentHeader?.manual_notes ?? null,
            file_name: currentHeader?.file_name ?? null,
          },
          patch,
          { additional_info: 'Módulo ADM • Despesas (cabeçalho NFS-e)' }
        )
      }
      setHeaderEditFileDownloadError(null)
      setHeaderEditModal(null)
      bumpDocumentsRefreshKey()
    } catch (error) {
      setPageError(getErrorMessage(error))
    } finally {
      setSubmittingHeaderEdit(false)
    }
  }

  const confirmDelete = async () => {
    if (!deleteModal || !user?.id) return
    if (!deleteReason.trim()) return

    const reason = deleteReason.trim()
    setSubmittingDelete(true)
    try {
      if (deleteModal.type === 'nfe') {
        await admDespesasService.softDeleteNfe(deleteModal.id, reason, user.id)
        setNfeList((prev) => prev.filter((n) => n.id !== deleteModal.id))
        await logDelete('other', deleteModal.id, `Excluiu NF-e: ${deleteModal.label}`, { additional_info: `Motivo: ${reason}` })
      } else if (deleteModal.type === 'nfse') {
        await admDespesasService.softDeleteNfse(deleteModal.id, reason, user.id)
        setNfseList((prev) => prev.filter((n) => n.id !== deleteModal.id))
        await logDelete('other', deleteModal.id, `Excluiu NFS-e: ${deleteModal.label}`, { additional_info: `Motivo: ${reason}` })
      } else {
        await admDespesasService.softDeleteWithoutNf(deleteModal.id, reason, user.id)
        setWithoutNfList((prev) => prev.filter((r) => r.id !== deleteModal.id))
        setWithoutNfDetailModal((prev) => (prev?.id === deleteModal.id ? null : prev))
        await logDelete('other', deleteModal.id, `Excluiu registro planilha (sem NF): ${deleteModal.label}`, { additional_info: `Motivo: ${reason}` })
      }
      closeDeleteModal()
    } catch (error) {
      setPageError(getErrorMessage(error))
    } finally {
      setSubmittingDelete(false)
    }
  }

  const openNfeDetail = async (nfe: AdmNfeHeader) => {
    setLoadingDetail(true)
    try {
      const items = await admDespesasService.listNfeItems(nfe.id)
      setDetailNfe({ header: nfe, items })
    } catch (error) {
      setPageError(getErrorMessage(error))
    } finally {
      setLoadingDetail(false)
    }
  }

  const openNfseDetail = async (nfse: AdmNfseHeader) => {
    setLoadingDetail(true)
    try {
      const items = await admDespesasService.listNfseItems(nfse.id)
      setDetailNfse({ header: nfse, items })
    } catch (error) {
      setPageError(getErrorMessage(error))
    } finally {
      setLoadingDetail(false)
    }
  }

  const openItemEditModal = (tableType: 'nfe' | 'nfse', item: AdmNfeItem | AdmNfseItem) => {
    const header =
      tableType === 'nfe'
        ? detailNfe?.header ?? nfeHeaderMap.get((item as AdmNfeItem).nfe_id)
        : detailNfse?.header ?? nfseHeaderMap.get((item as AdmNfseItem).nfse_id)
    if (!header) return
    setItemEditModal({
      tableType,
      itemId: item.id,
      headerId: header.id,
      headerNumber: header.number ?? header.id,
      description: item.description ?? '',
      ncm: tableType === 'nfe' ? ((item as AdmNfeItem).ncm ?? '') : '',
      unit: tableType === 'nfe' ? ((item as AdmNfeItem).unit ?? '') : '',
      serviceCode: tableType === 'nfse' ? ((item as AdmNfseItem).service_code ?? '') : '',
      quantity: item.quantity != null ? String(item.quantity) : '',
      unitValue: item.unit_value != null ? String(item.unit_value) : '',
      totalValue: item.total_value != null ? String(item.total_value) : '',
      categoryId: item.category_id ?? '',
      costCenterId: item.cost_center_id ?? '',
    })
  }

  const closeItemEditModal = () => {
    if (submittingItemEdit) return
    setItemEditModal(null)
  }

  const openWithoutNfEditModal = (row: AdmWithoutNfRow) => {
    setWithoutNfEditModal({
      id: row.id,
      paymentType: row.payment_type ?? '',
      expenseDate: row.expense_date ? row.expense_date.slice(0, 10) : '',
      description: row.description ?? '',
      installment: row.installment ?? '',
      amountPaid: row.amount_paid != null ? String(row.amount_paid) : '',
      amountToPay: row.amount_to_pay != null ? String(row.amount_to_pay) : '',
      notes: row.notes ?? '',
      paymentDate: row.payment_date ? row.payment_date.slice(0, 10) : '',
      manualNotes: row.manual_notes ?? '',
      categoryId: row.category_id ?? '',
      costCenterId: row.cost_center_id ?? '',
    })
  }

  const handleEditUnifiedDocument = (doc: UnifiedDocument) => {
    setPageError(null)
    if (doc.source === 'nfe') {
      const header = nfeList.find((item) => item.id === doc.id)
      if (!header) {
        setPageError('NF-e não encontrada. Recarregue a página e tente novamente.')
        return
      }
      openHeaderEditModal('nfe', header)
      return
    }
    if (doc.source === 'nfse') {
      const header = nfseList.find((item) => item.id === doc.id)
      if (!header) {
        setPageError('NFS-e não encontrada. Recarregue a página e tente novamente.')
        return
      }
      openHeaderEditModal('nfse', header)
      return
    }
    if (doc.source === 'sem_nf') {
      const row = withoutNfList.find((item) => item.id === doc.id)
      if (!row) {
        setPageError('Registro da planilha não encontrado. Recarregue a página e tente novamente.')
        return
      }
      openWithoutNfEditModal(row)
      return
    }
    if (doc.source === 'contracts') {
      const contract = contractsList.find((item) => item.id === doc.id)
      if (!contract) {
        setPageError('Contrato não encontrado. Recarregue a página e tente novamente.')
        return
      }
      setContractEditModal(contractEditStateFromRecord(contract))
      return
    }
    const budget = budgetList.find((item) => item.id === doc.id)
    if (!budget) {
      setPageError('Orçamento/pedido não encontrado. Recarregue a página e tente novamente.')
      return
    }
    setBudgetEditDownloadError(null)
    setBudgetEditModal(budgetEditStateFromRecord(budget))
  }

  const closeContractEditModal = () => {
    if (submittingContractEdit) return
    setContractEditModal(null)
  }

  const closeBudgetEditModal = () => {
    if (submittingBudgetEdit) return
    setBudgetEditDownloadError(null)
    setBudgetEditModal(null)
  }

  const openBudgetEditDownload = React.useCallback(async () => {
    if (!budgetEditModal) return
    if (!hasAdmNotaDownloadKey(budgetEditModal.fileNameStorage, budgetEditModal.fileName)) {
      setBudgetEditDownloadError('Informe file_name ou file_name_storage para baixar o documento.')
      return
    }
    setBudgetEditDownloadBusy(true)
    setBudgetEditDownloadError(null)
    setPageError(null)
    try {
      const r = await runAdmNotaSignedDownloadCore(
        budgetEditModal.fileNameStorage,
        budgetEditModal.fileName,
      )
      if (!r.ok) setBudgetEditDownloadError(r.message)
    } finally {
      setBudgetEditDownloadBusy(false)
    }
  }, [budgetEditModal, runAdmNotaSignedDownloadCore])

  const handleSaveContractEdit = async () => {
    if (!contractEditModal) return
    setSubmittingContractEdit(true)
    setPageError(null)
    try {
      const patch = contractEditPatchFromModal(contractEditModal)
      const prev = contractsList.find((x) => x.id === contractEditModal.id)
      await admDespesasService.updateContract(contractEditModal.id, patch)
      await logUpdate(
        'other',
        contractEditModal.id,
        `Atualizou contrato ${patch.contract_number ?? contractEditModal.id}`,
        {
          contract_number: prev?.contract_number ?? null,
          counterparty_name: prev?.counterparty_name ?? null,
          counterparty_document: prev?.counterparty_document ?? null,
          start_date: prev?.start_date ?? null,
          end_date: prev?.end_date ?? null,
          total_value: prev?.total_value ?? null,
          notes: prev?.notes ?? null,
          objective: prev?.objective ?? null,
          signature_date: prev?.signature_date ?? null,
          payment_notes: prev?.payment_notes ?? null,
          payment_date: prev?.payment_date ?? null,
          manual_notes: prev?.manual_notes ?? null,
          contractor_name: prev?.contractor_name ?? null,
          contractor_document: prev?.contractor_document ?? null,
          import_method: prev?.import_method ?? null,
          file_name: prev?.file_name ?? null,
          file_name_storage: prev?.file_name_storage ?? null,
          payload_json: prev?.payload_json ?? null,
        },
        patch,
        { additional_info: 'Módulo ADM • Despesas (contrato)' },
      )
      setContractsList((prev) =>
        prev.map((item) => (item.id === contractEditModal.id ? { ...item, ...patch } : item)),
      )
      setContractEditModal(null)
      bumpDocumentsRefreshKey()
    } catch (error) {
      setPageError(getErrorMessage(error))
    } finally {
      setSubmittingContractEdit(false)
    }
  }

  const handleSaveBudgetEdit = async () => {
    if (!budgetEditModal) return
    setSubmittingBudgetEdit(true)
    setPageError(null)
    try {
      const patch = budgetEditPatchFromModal(budgetEditModal)
      const prev = budgetList.find((x) => x.id === budgetEditModal.id)
      await admDespesasService.updateOrderHeader(budgetEditModal.id, patch)
      await logUpdate(
        'other',
        budgetEditModal.id,
        `Atualizou orçamento/pedido ${patch.number ?? budgetEditModal.id}`,
        {
          document_kind: prev?.document_kind ?? null,
          number: prev?.number ?? null,
          issue_date: prev?.issue_date ?? null,
          issuer_name: prev?.issuer_name ?? null,
          issuer_cnpj: prev?.issuer_cnpj ?? null,
          recipient_name: prev?.recipient_name ?? null,
          recipient_document: prev?.recipient_document ?? null,
          total_products: prev?.total_products ?? null,
          total_freight: prev?.total_freight ?? null,
          total_discount: prev?.total_discount ?? null,
          total_value: prev?.total_value ?? null,
          payment_method: prev?.payment_method ?? null,
          payment_date: prev?.payment_date ?? null,
          manual_notes: prev?.manual_notes ?? null,
          file_name: prev?.file_name ?? null,
          file_name_storage: prev?.file_name_storage ?? null,
        },
        patch,
        { additional_info: 'Módulo ADM • Despesas (orçamento/pedido)' },
      )
      setBudgetList((prev) =>
        prev.map((item) => (item.id === budgetEditModal.id ? { ...item, ...patch } : item)),
      )
      setBudgetEditDownloadError(null)
      setBudgetEditModal(null)
      bumpDocumentsRefreshKey()
    } catch (error) {
      setPageError(getErrorMessage(error))
    } finally {
      setSubmittingBudgetEdit(false)
    }
  }

  const handleViewUnifiedDocument = (doc: UnifiedDocument) => {
    setPageError(null)
    if (doc.source === 'nfe') {
      const header = nfeList.find((item) => item.id === doc.id)
      if (!header) {
        setPageError('NF-e não encontrada. Recarregue a página e tente novamente.')
        return
      }
      void openNfeDetail(header)
      return
    }
    if (doc.source === 'nfse') {
      const header = nfseList.find((item) => item.id === doc.id)
      if (!header) {
        setPageError('NFS-e não encontrada. Recarregue a página e tente novamente.')
        return
      }
      void openNfseDetail(header)
      return
    }
    if (doc.source === 'sem_nf') {
      const row = withoutNfList.find((item) => item.id === doc.id)
      if (!row) {
        setPageError('Registro da planilha não encontrado. Recarregue a página e tente novamente.')
        return
      }
      setWithoutNfDetailModal(row)
      return
    }
    if (doc.source === 'contracts') {
      const contract = contractsList.find((item) => item.id === doc.id)
      if (!contract) {
        setPageError('Contrato não encontrado. Recarregue a página e tente novamente.')
        return
      }
      setPendingUnifiedDetail({ source: 'contracts', id: doc.id })
      setActiveTab('contracts')
      return
    }
    const budget = budgetList.find((item) => item.id === doc.id)
    if (!budget) {
      setPageError('Orçamento/pedido não encontrado. Recarregue a página e tente novamente.')
      return
    }
    setPendingUnifiedDetail({ source: 'budgets', id: doc.id })
    setActiveTab('budgets')
  }

  const closeWithoutNfEditModal = () => {
    if (submittingWithoutNfEdit) return
    setWithoutNfEditModal(null)
  }

  const handleSaveWithoutNfEdit = async () => {
    if (!withoutNfEditModal) return
    const oldRow = withoutNfList.find((r) => r.id === withoutNfEditModal.id)
    if (!oldRow) return

    const categoryId = withoutNfEditModal.categoryId || null
    const costCenterId = withoutNfEditModal.costCenterId || null

    const patch = {
      payment_type: withoutNfEditModal.paymentType.trim() || null,
      cost_center_id: costCenterId,
      category_id: categoryId,
      expense_date: withoutNfEditModal.expenseDate.trim() || null,
      description: withoutNfEditModal.description.trim() || null,
      installment: withoutNfEditModal.installment.trim() || null,
      amount_paid: withoutNfEditModal.amountPaid.trim() ? Number(withoutNfEditModal.amountPaid) : null,
      amount_to_pay: withoutNfEditModal.amountToPay.trim() ? Number(withoutNfEditModal.amountToPay) : null,
      notes: withoutNfEditModal.notes.trim() || null,
      payment_date: withoutNfEditModal.paymentDate.trim() || null,
      manual_notes: withoutNfEditModal.manualNotes.trim() || null,
    }

    const oldPayload = {
      payment_type: oldRow.payment_type ?? null,
      cost_center_id: oldRow.cost_center_id ?? null,
      category_id: oldRow.category_id ?? null,
      expense_date: oldRow.expense_date ? oldRow.expense_date.slice(0, 10) : null,
      description: oldRow.description ?? null,
      installment: oldRow.installment ?? null,
      amount_paid: oldRow.amount_paid ?? null,
      amount_to_pay: oldRow.amount_to_pay ?? null,
      notes: oldRow.notes ?? null,
      payment_date: oldRow.payment_date ? oldRow.payment_date.slice(0, 10) : null,
      manual_notes: oldRow.manual_notes ?? null,
    }

    if (JSON.stringify(oldPayload) === JSON.stringify(patch)) {
      setWithoutNfEditModal(null)
      return
    }

    setSubmittingWithoutNfEdit(true)
    try {
      await admDespesasService.updateWithoutNf(withoutNfEditModal.id, patch)
      setWithoutNfList((prev) => prev.map((r) => (r.id === withoutNfEditModal.id ? { ...r, ...patch } : r)))
      setWithoutNfDetailModal((prev) =>
        prev && prev.id === withoutNfEditModal.id ? { ...prev, ...patch } : prev,
      )
      await logUpdate(
        'other',
        withoutNfEditModal.id,
        'Atualizou registro da planilha (sem NF)',
        oldPayload,
        patch,
        { additional_info: 'Módulo ADM • Despesas • Planilha sem NF' },
      )
      setWithoutNfEditModal(null)
      bumpDocumentsRefreshKey()
    } catch (error) {
      setPageError(getErrorMessage(error))
    } finally {
      setSubmittingWithoutNfEdit(false)
    }
  }

  const handleSaveItemEdit = async () => {
    if (!itemEditModal) return
    const categoryId = itemEditModal.categoryId || null
    const costCenterId = itemEditModal.costCenterId || null

    const oldItem = itemEditModal.tableType === 'nfe'
      ? allNfeItems.find((i) => i.id === itemEditModal.itemId)
      : allNfseItems.find((i) => i.id === itemEditModal.itemId)

    if (!oldItem) return

    setSubmittingItemEdit(true)
    try {
      if (itemEditModal.tableType === 'nfe') {
        const patch = {
          description: itemEditModal.description.trim() || null,
          ncm: itemEditModal.ncm.trim() || null,
          quantity: itemEditModal.quantity.trim() ? Number(itemEditModal.quantity) : null,
          unit: itemEditModal.unit.trim() || null,
          unit_value: itemEditModal.unitValue.trim() ? Number(itemEditModal.unitValue) : null,
          total_value: itemEditModal.totalValue.trim() ? Number(itemEditModal.totalValue) : null,
          category_id: categoryId,
          cost_center_id: costCenterId,
        }

        const oldPayload = {
          description: oldItem.description ?? null,
          ncm: (oldItem as AdmNfeItem).ncm ?? null,
          quantity: oldItem.quantity ?? null,
          unit: (oldItem as AdmNfeItem).unit ?? null,
          unit_value: oldItem.unit_value ?? null,
          total_value: oldItem.total_value ?? null,
          category_id: oldItem.category_id ?? null,
          cost_center_id: oldItem.cost_center_id ?? null,
        }

        if (JSON.stringify(oldPayload) === JSON.stringify(patch)) {
          setItemEditModal(null)
          return
        }

        await admDespesasService.updateNfeItem(itemEditModal.itemId, patch)
        setDetailNfe((prev) => prev ? {
          ...prev,
          items: prev.items.map((i) => i.id === itemEditModal.itemId ? { ...i, ...patch } : i),
        } : prev)
        setAllNfeItems((prev) => prev.map((i) => i.id === itemEditModal.itemId ? { ...i, ...patch } : i))

        await logUpdate(
          'other',
          itemEditModal.itemId,
          `Atualizou dados do item da NFE ${itemEditModal.headerNumber}`,
          oldPayload,
          patch,
          {
            additional_info: `NFE ID: ${itemEditModal.headerId} | Item: ${itemEditModal.description || itemEditModal.itemId}`,
          }
        )
      } else {
        const patch = {
          description: itemEditModal.description.trim() || null,
          service_code: itemEditModal.serviceCode.trim() || null,
          quantity: itemEditModal.quantity.trim() ? Number(itemEditModal.quantity) : null,
          unit_value: itemEditModal.unitValue.trim() ? Number(itemEditModal.unitValue) : null,
          total_value: itemEditModal.totalValue.trim() ? Number(itemEditModal.totalValue) : null,
          category_id: categoryId,
          cost_center_id: costCenterId,
        }

        const oldPayload = {
          description: oldItem.description ?? null,
          service_code: (oldItem as AdmNfseItem).service_code ?? null,
          quantity: oldItem.quantity ?? null,
          unit_value: oldItem.unit_value ?? null,
          total_value: oldItem.total_value ?? null,
          category_id: oldItem.category_id ?? null,
          cost_center_id: oldItem.cost_center_id ?? null,
        }

        if (JSON.stringify(oldPayload) === JSON.stringify(patch)) {
          setItemEditModal(null)
          return
        }

        await admDespesasService.updateNfseItem(itemEditModal.itemId, patch)
        setDetailNfse((prev) => prev ? {
          ...prev,
          items: prev.items.map((i) => i.id === itemEditModal.itemId ? { ...i, ...patch } : i),
        } : prev)
        setAllNfseItems((prev) => prev.map((i) => i.id === itemEditModal.itemId ? { ...i, ...patch } : i))

        await logUpdate(
          'other',
          itemEditModal.itemId,
          `Atualizou dados do serviço da NFSE ${itemEditModal.headerNumber}`,
          oldPayload,
          patch,
          {
            additional_info: `NFSE ID: ${itemEditModal.headerId} | Item: ${itemEditModal.description || itemEditModal.itemId}`,
          }
        )
      }

      setItemEditModal(null)
    } catch (error) {
      setPageError(getErrorMessage(error))
    } finally {
      setSubmittingItemEdit(false)
    }
  }

  const handleSoftDeleteItem = async (tableType: 'nfe' | 'nfse', item: AdmNfeItem | AdmNfseItem) => {
    if (!user?.id) return
    const header =
      tableType === 'nfe'
        ? nfeHeaderMap.get((item as AdmNfeItem).nfe_id) ??
          (detailNfe?.header?.id === (item as AdmNfeItem).nfe_id ? detailNfe.header : undefined)
        : nfseHeaderMap.get((item as AdmNfseItem).nfse_id) ??
          (detailNfse?.header?.id === (item as AdmNfseItem).nfse_id ? detailNfse.header : undefined)
    if (!header) return

    const confirmed = window.confirm(`Confirma a exclusão do item "${item.description ?? item.id}"?`)
    if (!confirmed) return

    setSavingItem(item.id)
    try {
      if (tableType === 'nfe') {
        await admDespesasService.softDeleteNfeItem(item.id, user.id)
        setDetailNfe((prev) => prev ? { ...prev, items: prev.items.filter((i) => i.id !== item.id) } : prev)
        setAllNfeItems((prev) => prev.filter((i) => i.id !== item.id))
      } else {
        await admDespesasService.softDeleteNfseItem(item.id, user.id)
        setDetailNfse((prev) => prev ? { ...prev, items: prev.items.filter((i) => i.id !== item.id) } : prev)
        setAllNfseItems((prev) => prev.filter((i) => i.id !== item.id))
      }

      await logDelete(
        'other',
        item.id,
        `Excluiu item da ${tableType.toUpperCase()} ${header.number ?? header.id}`,
        {
          additional_info: `${tableType.toUpperCase()} ID: ${header.id} | Item: ${item.description ?? item.id}`,
          old_values: {
            description: item.description ?? null,
            category_id: item.category_id ?? null,
            cost_center_id: item.cost_center_id ?? null,
          },
        }
      )
    } catch (error) {
      setPageError(getErrorMessage(error))
    } finally {
      setSavingItem(null)
    }
  }

  const openUploadModal = () => {
    setCadastroSource('notas_fiscais')
    setUploadFiles([])
    setExcelFileName(null)
    setExcelArrayBuffer(null)
    setExcelSheetNames([])
    setExcelSelectedSheet('')
    setExcelHeaderRow('1')
    setExcelFirstDataRow('2')
    setExcelParsedRows(null)
    setExcelParseError(null)
    setSpreadsheetImportDone(false)
    setIsProcessing(false)
    setProcessingDone(false)
    setProcessingStartedAt(null)
    setProcessingFinishedAt(null)
    setUploadModalOpen(true)
  }

  const closeUploadModal = () => {
    if (isProcessing || spreadsheetSubmitting) return
    setUploadModalOpen(false)
    setUploadFiles([])
    setCadastroSource('notas_fiscais')
    setExcelFileName(null)
    setExcelArrayBuffer(null)
    setExcelSheetNames([])
    setExcelSelectedSheet('')
    setExcelHeaderRow('1')
    setExcelFirstDataRow('2')
    setExcelParsedRows(null)
    setExcelParseError(null)
    setSpreadsheetImportDone(false)
    setIsProcessing(false)
    setProcessingDone(false)
    setProcessingStartedAt(null)
    setProcessingFinishedAt(null)
    void loadData()
  }

  React.useEffect(() => {
    if (cadastroSource !== 'planilha_pagamentos' || !excelArrayBuffer || !excelSelectedSheet) return
    const parsedHeader = parseInt(excelHeaderRow.trim(), 10)
    const headerRow = Number.isFinite(parsedHeader) && parsedHeader >= 1 ? parsedHeader : 1
    const parsedData = parseInt(excelFirstDataRow.trim(), 10)
    const firstDataRow = Number.isFinite(parsedData) && parsedData >= 1 ? parsedData : 2
    const result = parsePlanilhaPagamentosFile(excelArrayBuffer, categorias, centrosCusto, {
      sheetName: excelSelectedSheet,
      headerRow,
      firstDataRow,
    })
    if (!result.ok) {
      setExcelParseError(result.message)
      setExcelParsedRows(null)
      return
    }
    setExcelParseError(null)
    setExcelParsedRows(result.rows)
  }, [cadastroSource, excelArrayBuffer, excelSelectedSheet, excelHeaderRow, excelFirstDataRow, categorias, centrosCusto])

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files ?? [])
    if (selected.length === 0) return

    let filtered: File[] = selected
    if (cadastroSource === 'notas_fiscais') {
      filtered = selected.filter(isPdfFileForUpload)
      if (filtered.length === 0) {
        if (selected.length > 0) setPageError('Selecione apenas arquivos PDF.')
        e.target.value = ''
        return
      }
    } else if (isCadastroUploadContratosTipo(cadastroSource)) {
      filtered = selected.filter(isPdfOrImageFileForContratos)
      if (filtered.length === 0) {
        if (selected.length > 0) {
          setPageError('Selecione apenas arquivos PDF ou imagens (JPEG, PNG, GIF, WebP, etc.).')
        }
        e.target.value = ''
        return
      }
    }

    setPageError(null)
    setUploadFiles((prev) => {
      const existingNames = new Set(prev.map((f) => f.file.name))
      const newEntries: FileStatus[] = filtered
        .filter((f) => !existingNames.has(f.name))
        .map((f) => ({ file: f, status: 'pending' }))
      return [...prev, ...newEntries]
    })
    e.target.value = ''
  }

  const handleCadastroSourceChange = (src: CadastroSource) => {
    setCadastroSource(src)
    setExcelFileName(null)
    setExcelArrayBuffer(null)
    setExcelSheetNames([])
    setExcelSelectedSheet('')
    setExcelHeaderRow('1')
    setExcelFirstDataRow('2')
    setExcelParsedRows(null)
    setExcelParseError(null)
    setSpreadsheetImportDone(false)
    setUploadFiles([])
    setProcessingDone(false)
    setIsProcessing(false)
    setProcessingStartedAt(null)
    setProcessingFinishedAt(null)
  }

  const handleExcelFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    setExcelParseError(null)
    setExcelParsedRows(null)
    setSpreadsheetImportDone(false)
    if (!file) return
    const lower = file.name.toLowerCase()
    if (!lower.endsWith('.xlsx') && !lower.endsWith('.xls')) {
      setExcelParseError('Selecione um arquivo Excel (.xlsx ou .xls).')
      setExcelFileName(null)
      setExcelArrayBuffer(null)
      setExcelSheetNames([])
      setExcelSelectedSheet('')
      return
    }
    setExcelFileName(file.name)
    void (async () => {
      try {
        const buf = await file.arrayBuffer()
        const sheets = getExcelSheetNamesFromBuffer(buf)
        if (!sheets.ok) {
          setExcelParseError(sheets.message)
          setExcelParsedRows(null)
          setExcelArrayBuffer(null)
          setExcelSheetNames([])
          setExcelSelectedSheet('')
          return
        }
        setExcelSheetNames(sheets.names)
        setExcelSelectedSheet(sheets.names[0] ?? '')
        setExcelArrayBuffer(buf)
      } catch (err) {
        setExcelParseError(err instanceof Error ? err.message : 'Erro ao ler a planilha.')
        setExcelParsedRows(null)
        setExcelArrayBuffer(null)
        setExcelSheetNames([])
        setExcelSelectedSheet('')
      }
    })()
  }

  const handleSpreadsheetSubmit = async () => {
    if (!excelParsedRows?.length || spreadsheetSubmitting) return
    if (!excelArrayBuffer || !excelFileName) {
      setPageError('Arquivo da planilha não está disponível para envio. Selecione o Excel novamente.')
      return
    }
    setSpreadsheetSubmitting(true)
    setPageError(null)
    const batchId = crypto.randomUUID()
    const sourceName = excelFileName
    const mime = sourceName.toLowerCase().endsWith('.xls')
      ? 'application/vnd.ms-excel'
      : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    const spreadsheetFile = new File(
      [new Blob([excelArrayBuffer], { type: mime })],
      sourceName,
      { type: mime },
    )
    try {
      await sendFile(spreadsheetFile, CADASTRAR_DESPESAS_WEBHOOK_URL, 'planilha_pagamentos')
    } catch (error) {
      setPageError(getErrorMessage(error))
      setSpreadsheetSubmitting(false)
      return
    }
    const rows: AdmWithoutNfInsert[] = excelParsedRows.map((r) => ({
      import_batch_id: batchId,
      source_file_name: sourceName,
      payment_type: r.payment_type,
      cost_center_id: r.cost_center_id,
      category_id: r.category_id,
      expense_date: r.expense_date,
      description: r.description,
      installment: r.installment,
      amount_paid: r.amount_paid,
      amount_to_pay: r.amount_to_pay,
      notes: r.notes,
      payment_date: null,
      manual_notes: null,
      uploaded_by: user?.id ?? null,
    }))
    try {
      await admDespesasService.insertWithoutNfBatch(rows)
      await logUserAction({
        action: 'import',
        entityType: 'other',
        entityId: batchId,
        description: `Importou planilha (sem NF): ${rows.length} linha(s) — ${sourceName}${excelSelectedSheet ? ` (aba: ${excelSelectedSheet})` : ''}`,
        metadata: {
          additional_info: 'Módulo ADM • Despesas • Planilha sem NF',
          new_values: { row_count: rows.length, import_batch_id: batchId, sheet: excelSelectedSheet || null },
        },
      })
      setSpreadsheetImportDone(true)
      setExcelParsedRows(null)
      setExcelFileName(null)
      setExcelArrayBuffer(null)
      setExcelSheetNames([])
      setExcelSelectedSheet('')
      setExcelHeaderRow('1')
      setExcelFirstDataRow('2')
      void loadData()
    } catch (error) {
      setPageError(getErrorMessage(error))
    } finally {
      setSpreadsheetSubmitting(false)
    }
  }

  const removeFile = (index: number) => {
    setUploadFiles((prev) => prev.filter((_, i) => i !== index))
  }

  const sendFile = async (file: File, webhookUrl: string, fluxoCadastro: CadastroSource): Promise<void> => {
    const formData = new FormData()
    formData.append('arquivo', file)
    formData.append('nome_arquivo', file.name)
    formData.append('cadastro_fluxo', fluxoCadastro)
    const tipo = getTipoParamForCadastro(fluxoCadastro)
    if (tipo) {
      formData.append('tipo', tipo)
    }

    const response = await fetch(webhookUrl, {
      method: 'POST',
      body: formData,
    })

    if (!response.ok) throw new Error(`HTTP ${response.status}`)

    let retorno = 'sucesso'
    try {
      const json = await response.json() as Array<{ retorno?: string }> | { retorno?: string }
      const first = Array.isArray(json) ? json[0] : json
      retorno = (first?.retorno ?? 'sucesso').toString().trim()
    } catch {
      // resposta sem JSON válido — trata como sucesso
    }

    if (retorno.toLowerCase() !== 'sucesso') {
      throw new Error(retorno)
    }
  }

  const handleUploadSubmit = async () => {
    if (uploadFiles.length === 0 || isProcessing) return
    const webhookUrl = CADASTRAR_DESPESAS_WEBHOOK_URL
    const processedByNameCache = new Map<string, boolean>()
    setIsProcessing(true)
    setProcessingDone(false)
    setProcessingStartedAt(new Date())
    setProcessingFinishedAt(null)

    const uploadSessionOutcomes: { fileName: string; success: boolean; error?: string }[] = []

    for (let i = 0; i < uploadFiles.length; i++) {
      const entry = uploadFiles[i]
      if (entry.status === 'success') continue

      if (i > 0) {
        await new Promise<void>((resolve) => setTimeout(resolve, 1000))
      }

      setUploadFiles((prev) =>
        prev.map((f, idx) => (idx === i ? { ...f, status: 'sending', error: undefined } : f))
      )

      try {
        const cacheKey = `${cadastroSource}:${entry.file.name}`
        const alreadyProcessedCached = processedByNameCache.get(cacheKey)
        const alreadyProcessed = alreadyProcessedCached ?? await isArquivoJaProcessado(cadastroSource, entry.file.name)
        processedByNameCache.set(cacheKey, alreadyProcessed)
        if (alreadyProcessed) {
          const duplicateMsg = 'Arquivo já processado anteriormente (nome já existente).'
          setUploadFiles((prev) =>
            prev.map((f, idx) => (idx === i ? { ...f, status: 'error', error: duplicateMsg, finishedAt: new Date() } : f))
          )
          uploadSessionOutcomes.push({ fileName: entry.file.name, success: false, error: duplicateMsg })
          continue
        }

        await sendFile(entry.file, webhookUrl, cadastroSource)
        setUploadFiles((prev) =>
          prev.map((f, idx) => (idx === i ? { ...f, status: 'success', finishedAt: new Date() } : f))
        )
        uploadSessionOutcomes.push({ fileName: entry.file.name, success: true })
      } catch (error) {
        const msg = error instanceof Error ? error.message : 'Erro ao enviar.'
        const isNetworkError = msg === 'Failed to fetch'

        if (isNetworkError) {
          setUploadFiles((prev) =>
            prev.map((f, idx) => (idx === i ? { ...f, status: 'sending', error: undefined } : f))
          )
          await new Promise<void>((resolve) => setTimeout(resolve, 5000))
          try {
            await sendFile(entry.file, webhookUrl, cadastroSource)
            setUploadFiles((prev) =>
              prev.map((f, idx) => (idx === i ? { ...f, status: 'success', finishedAt: new Date() } : f))
            )
            uploadSessionOutcomes.push({ fileName: entry.file.name, success: true })
          } catch (retryError) {
            const retryMsg = retryError instanceof Error ? retryError.message : 'Erro ao enviar.'
            setUploadFiles((prev) =>
              prev.map((f, idx) => (idx === i ? { ...f, status: 'error', error: retryMsg, finishedAt: new Date() } : f))
            )
            uploadSessionOutcomes.push({ fileName: entry.file.name, success: false, error: retryMsg })
          }
        } else {
          setUploadFiles((prev) =>
            prev.map((f, idx) => (idx === i ? { ...f, status: 'error', error: msg, finishedAt: new Date() } : f))
          )
          uploadSessionOutcomes.push({ fileName: entry.file.name, success: false, error: msg })
        }
      }
    }

    if (uploadSessionOutcomes.length > 0) {
      const ok = uploadSessionOutcomes.filter((o) => o.success)
      const fail = uploadSessionOutcomes.filter((o) => !o.success)
      const fluxoLabel =
        cadastroSource === 'contratos'
          ? 'Contratos (webhook)'
          : cadastroSource === 'orcamentos_pedidos'
            ? 'Orçamentos/pedidos (webhook)'
            : 'Notas fiscais (webhook)'
      await logUserAction({
        action: 'import',
        entityType: 'other',
        entityId: null,
        description: `Envio de arquivos — ${fluxoLabel}: ${ok.length} sucesso(s)${fail.length ? `, ${fail.length} falha(s)` : ''}`,
        metadata: {
          additional_info: 'Módulo ADM • Despesas • Cadastrar despesas (upload)',
          new_values: {
            fluxo_cadastro: cadastroSource,
            arquivos_enviados_ok: ok.map((o) => o.fileName),
            falhas: fail.map((o) => ({ arquivo: o.fileName, erro: o.error ?? null })),
          },
        },
      })
    }

    const finishedAt = new Date()
    setProcessingFinishedAt(finishedAt)
    setIsProcessing(false)
    setProcessingDone(true)
    void loadData()
  }

  const tabClass = (tab: ActiveTab) =>
    `py-3 px-1 border-b-2 font-medium text-sm ${
      activeTab === tab
        ? 'border-primary-600 text-primary-600 dark:text-primary-400'
        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'
    }`

  const subTabClass = (active: boolean) =>
    `py-3 px-1 border-b-2 font-medium text-sm ${
      active
        ? 'border-primary-500 text-primary-600 dark:text-primary-400'
        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
    }`

  // Lógica de filtros e paginação - Notas NF-e
  const filteredNfeNotes = React.useMemo(() => {
    return nfeList.filter((nfe) => {
      const issuerLower = nfeItemsIssuer.toLowerCase()
      const numberLower = nfeItemsNoteNumber.toLowerCase()
      const accessKeyNormalized = nfeItemsAccessKey.replace(/\W/g, '').toLowerCase()
      if (issuerLower && !(nfe.issuer_name ?? '').toLowerCase().includes(issuerLower)) return false
      if (numberLower && !(nfe.number ?? '').toLowerCase().includes(numberLower)) return false
      if (accessKeyNormalized && !((nfe.access_key ?? '').replace(/\W/g, '').toLowerCase().includes(accessKeyNormalized))) return false
      if (!fileNameQueryMatches(nfe.file_name, nfeItemsFileName)) return false
      const itemLevel =
        nfeItemsSearch.trim() || nfeItemsCategory || nfeItemsCostCenter
      if (itemLevel) {
        const q = nfeItemsSearch.trim().toLowerCase()
        const itemsThis = allNfeItems.filter((i) => i.nfe_id === nfe.id)
        if (itemsThis.length === 0) return false
        const any = itemsThis.some((it) => {
          if (q && !(it.description ?? '').toLowerCase().includes(q)) return false
          if (nfeItemsCategory && it.category_id !== nfeItemsCategory) return false
          if (nfeItemsCostCenter && it.cost_center_id !== nfeItemsCostCenter) return false
          return true
        })
        if (!any) return false
      }
      if (!matchesPaymentDatePeriod(nfe.payment_date, nfePaymentDateFrom, nfePaymentDateTo)) return false
      return true
    })
  }, [nfeList, nfeItemsIssuer, nfeItemsNoteNumber, nfeItemsAccessKey, nfeItemsFileName, allNfeItems, nfeItemsSearch, nfeItemsCategory, nfeItemsCostCenter, nfePaymentDateFrom, nfePaymentDateTo])

  const sortedNfeNotes = React.useMemo(() => {
    return [...filteredNfeNotes].sort((a, b) => {
      let av: string | number = ''
      let bv: string | number = ''
      if (nfeNotesSort === 'number') { av = a.number ?? ''; bv = b.number ?? '' }
      else if (nfeNotesSort === 'issuer_name') { av = (a.issuer_name ?? '').toLowerCase(); bv = (b.issuer_name ?? '').toLowerCase() }
      else if (nfeNotesSort === 'issue_date') { av = a.issue_date ?? ''; bv = b.issue_date ?? '' }
      else if (nfeNotesSort === 'total_value') { av = a.total_value ?? 0; bv = b.total_value ?? 0 }
      if (av < bv) return nfeNotesSortOrder === 'asc' ? -1 : 1
      if (av > bv) return nfeNotesSortOrder === 'asc' ? 1 : -1
      return 0
    })
  }, [filteredNfeNotes, nfeNotesSort, nfeNotesSortOrder])

  const nfeNotesPagesCount = Math.max(1, Math.ceil(sortedNfeNotes.length / PAGE_SIZE))
  const nfeNotesStart = (nfeNotesPage - 1) * PAGE_SIZE
  const paginatedNfeNotes = sortedNfeNotes.slice(nfeNotesStart, nfeNotesStart + PAGE_SIZE)

  // Lógica de filtros e paginação - Itens NF-e
  const nfeHeaderMap = React.useMemo(() => new Map(nfeList.map((h) => [h.id, h])), [nfeList])

  const filteredNfeItems = React.useMemo(() => {
    return allNfeItems.filter((item) => {
      const header = nfeHeaderMap.get(item.nfe_id)
      if (!header) return false
      const searchLower = nfeItemsSearch.toLowerCase()
      const issuerLower = nfeItemsIssuer.toLowerCase()
      const noteNumberLower = nfeItemsNoteNumber.toLowerCase()
      const accessKeyNormalized = nfeItemsAccessKey.replace(/\W/g, '').toLowerCase()
      if (searchLower && !(item.description ?? '').toLowerCase().includes(searchLower)) return false
      if (issuerLower && !(header?.issuer_name ?? '').toLowerCase().includes(issuerLower)) return false
      if (noteNumberLower && !(header?.number ?? '').toLowerCase().includes(noteNumberLower)) return false
      if (accessKeyNormalized && !((header?.access_key ?? '').replace(/\W/g, '').toLowerCase().includes(accessKeyNormalized))) return false
      if (!fileNameQueryMatches(header.file_name, nfeItemsFileName)) return false
      if (nfeItemsCategory && item.category_id !== nfeItemsCategory) return false
      if (nfeItemsCostCenter && item.cost_center_id !== nfeItemsCostCenter) return false
      if (!matchesPaymentDatePeriod(header.payment_date, nfePaymentDateFrom, nfePaymentDateTo)) return false
      return true
    })
  }, [allNfeItems, nfeHeaderMap, nfeItemsSearch, nfeItemsIssuer, nfeItemsNoteNumber, nfeItemsAccessKey, nfeItemsFileName, nfeItemsCategory, nfeItemsCostCenter, nfePaymentDateFrom, nfePaymentDateTo])

  const sortedNfeItems = React.useMemo(() => {
    return [...filteredNfeItems].sort((a, b) => {
      const ha = nfeHeaderMap.get(a.nfe_id)
      const hb = nfeHeaderMap.get(b.nfe_id)
      let av: string | number = ''
      let bv: string | number = ''
      if (nfeItemsSort === 'number') { av = ha?.number ?? ''; bv = hb?.number ?? '' }
      else if (nfeItemsSort === 'issuer_name') { av = (ha?.issuer_name ?? '').toLowerCase(); bv = (hb?.issuer_name ?? '').toLowerCase() }
      else if (nfeItemsSort === 'description') { av = (a.description ?? '').toLowerCase(); bv = (b.description ?? '').toLowerCase() }
      else if (nfeItemsSort === 'quantity') { av = a.quantity ?? 0; bv = b.quantity ?? 0 }
      else if (nfeItemsSort === 'unit_value') { av = a.unit_value ?? 0; bv = b.unit_value ?? 0 }
      else if (nfeItemsSort === 'total_value') { av = a.total_value ?? 0; bv = b.total_value ?? 0 }
      if (av < bv) return nfeItemsSortOrder === 'asc' ? -1 : 1
      if (av > bv) return nfeItemsSortOrder === 'asc' ? 1 : -1
      return 0
    })
  }, [filteredNfeItems, nfeHeaderMap, nfeItemsSort, nfeItemsSortOrder])

  const nfeItemsPagesCount = Math.max(1, Math.ceil(sortedNfeItems.length / PAGE_SIZE))
  const nfeItemsStart = (nfeItemsPage - 1) * PAGE_SIZE
  const paginatedNfeItems = sortedNfeItems.slice(nfeItemsStart, nfeItemsStart + PAGE_SIZE)

  const nfeItemsCountByHeader = React.useMemo(() => {
    return allNfeItems.reduce((acc, item) => {
      acc.set(item.nfe_id, (acc.get(item.nfe_id) ?? 0) + 1)
      return acc
    }, new Map<string, number>())
  }, [allNfeItems])

  // Lógica de filtros e paginação - Notas NFS-e
  const filteredNfseNotes = React.useMemo(() => {
    return nfseList.filter((nfse) => {
      const providerLower = nfseItemsProvider.toLowerCase()
      const numberLower = nfseItemsNoteNumber.toLowerCase()
      const accessKeyNormalized = nfseItemsAccessKey.replace(/\W/g, '').toLowerCase()
      if (providerLower && !(nfse.provider_name ?? '').toLowerCase().includes(providerLower)) return false
      if (numberLower && !(nfse.number ?? '').toLowerCase().includes(numberLower)) return false
      if (accessKeyNormalized && !((nfse.access_key ?? '').replace(/\W/g, '').toLowerCase().includes(accessKeyNormalized))) return false
      if (!fileNameQueryMatches(nfse.file_name, nfseItemsFileName)) return false
      const itemLevel =
        nfseItemsSearch.trim() ||
        nfseItemsServiceCode.trim() ||
        nfseItemsCategory ||
        nfseItemsCostCenter
      if (itemLevel) {
        const q = nfseItemsSearch.trim().toLowerCase()
        const sc = nfseItemsServiceCode.trim().toLowerCase()
        const itemsThis = allNfseItems.filter((i) => i.nfse_id === nfse.id)
        if (itemsThis.length === 0) return false
        const any = itemsThis.some((it) => {
          if (q && !(it.description ?? '').toLowerCase().includes(q)) return false
          if (sc && !(it.service_code ?? '').toLowerCase().includes(sc)) return false
          if (nfseItemsCategory && it.category_id !== nfseItemsCategory) return false
          if (nfseItemsCostCenter && it.cost_center_id !== nfseItemsCostCenter) return false
          return true
        })
        if (!any) return false
      }
      if (!matchesPaymentDatePeriod(nfse.payment_date, nfsePaymentDateFrom, nfsePaymentDateTo)) return false
      return true
    })
  }, [
    nfseList,
    nfseItemsProvider,
    nfseItemsNoteNumber,
    nfseItemsAccessKey,
    nfseItemsFileName,
    allNfseItems,
    nfseItemsSearch,
    nfseItemsServiceCode,
    nfseItemsCategory,
    nfseItemsCostCenter,
    nfsePaymentDateFrom,
    nfsePaymentDateTo,
  ])

  const sortedNfseNotes = React.useMemo(() => {
    return [...filteredNfseNotes].sort((a, b) => {
      let av: string | number = ''
      let bv: string | number = ''
      if (nfseNotesSort === 'number') { av = a.number ?? ''; bv = b.number ?? '' }
      else if (nfseNotesSort === 'provider_name') { av = (a.provider_name ?? '').toLowerCase(); bv = (b.provider_name ?? '').toLowerCase() }
      else if (nfseNotesSort === 'issue_date') { av = a.issue_date ?? ''; bv = b.issue_date ?? '' }
      else if (nfseNotesSort === 'net_value') { av = getNfseDisplayTotal(a) ?? 0; bv = getNfseDisplayTotal(b) ?? 0 }
      if (av < bv) return nfseNotesSortOrder === 'asc' ? -1 : 1
      if (av > bv) return nfseNotesSortOrder === 'asc' ? 1 : -1
      return 0
    })
  }, [filteredNfseNotes, nfseNotesSort, nfseNotesSortOrder])

  const nfseNotesPagesCount = Math.max(1, Math.ceil(sortedNfseNotes.length / PAGE_SIZE))
  const nfseNotesStart = (nfseNotesPage - 1) * PAGE_SIZE
  const paginatedNfseNotes = sortedNfseNotes.slice(nfseNotesStart, nfseNotesStart + PAGE_SIZE)

  // Lógica de filtros e paginação - Itens NFS-e
  const nfseHeaderMap = React.useMemo(() => new Map(nfseList.map((h) => [h.id, h])), [nfseList])

  const filteredNfseItems = React.useMemo(() => {
    return allNfseItems.filter((item) => {
      const header = nfseHeaderMap.get(item.nfse_id)
      if (!header) return false
      const searchLower = nfseItemsSearch.toLowerCase()
      const providerLower = nfseItemsProvider.toLowerCase()
      const noteNumberLower = nfseItemsNoteNumber.toLowerCase()
      const accessKeyNormalized = nfseItemsAccessKey.replace(/\W/g, '').toLowerCase()
      const serviceCodeLower = nfseItemsServiceCode.toLowerCase()
      if (searchLower && !(item.description ?? '').toLowerCase().includes(searchLower)) return false
      if (providerLower && !(header?.provider_name ?? '').toLowerCase().includes(providerLower)) return false
      if (noteNumberLower && !(header?.number ?? '').toLowerCase().includes(noteNumberLower)) return false
      if (accessKeyNormalized && !((header?.access_key ?? '').replace(/\W/g, '').toLowerCase().includes(accessKeyNormalized))) return false
      if (!fileNameQueryMatches(header.file_name, nfseItemsFileName)) return false
      if (serviceCodeLower && !(item.service_code ?? '').toLowerCase().includes(serviceCodeLower)) return false
      if (nfseItemsCategory && item.category_id !== nfseItemsCategory) return false
      if (nfseItemsCostCenter && item.cost_center_id !== nfseItemsCostCenter) return false
      if (!matchesPaymentDatePeriod(header.payment_date, nfsePaymentDateFrom, nfsePaymentDateTo)) return false
      return true
    })
  }, [allNfseItems, nfseHeaderMap, nfseItemsSearch, nfseItemsProvider, nfseItemsNoteNumber, nfseItemsAccessKey, nfseItemsFileName, nfseItemsServiceCode, nfseItemsCategory, nfseItemsCostCenter, nfsePaymentDateFrom, nfsePaymentDateTo])

  const sortedNfseItems = React.useMemo(() => {
    return [...filteredNfseItems].sort((a, b) => {
      const ha = nfseHeaderMap.get(a.nfse_id)
      const hb = nfseHeaderMap.get(b.nfse_id)
      let av: string | number = ''
      let bv: string | number = ''
      if (nfseItemsSort === 'number') { av = ha?.number ?? ''; bv = hb?.number ?? '' }
      else if (nfseItemsSort === 'provider_name') { av = (ha?.provider_name ?? '').toLowerCase(); bv = (hb?.provider_name ?? '').toLowerCase() }
      else if (nfseItemsSort === 'description') { av = (a.description ?? '').toLowerCase(); bv = (b.description ?? '').toLowerCase() }
      else if (nfseItemsSort === 'quantity') { av = a.quantity ?? 0; bv = b.quantity ?? 0 }
      else if (nfseItemsSort === 'unit_value') { av = a.unit_value ?? 0; bv = b.unit_value ?? 0 }
      else if (nfseItemsSort === 'total_value') { av = a.total_value ?? 0; bv = b.total_value ?? 0 }
      if (av < bv) return nfseItemsSortOrder === 'asc' ? -1 : 1
      if (av > bv) return nfseItemsSortOrder === 'asc' ? 1 : -1
      return 0
    })
  }, [filteredNfseItems, nfseHeaderMap, nfseItemsSort, nfseItemsSortOrder])

  const nfseItemsPagesCount = Math.max(1, Math.ceil(sortedNfseItems.length / PAGE_SIZE))
  const nfseItemsStart = (nfseItemsPage - 1) * PAGE_SIZE
  const paginatedNfseItems = sortedNfseItems.slice(nfseItemsStart, nfseItemsStart + PAGE_SIZE)

  const filteredSemNf = React.useMemo(() => {
    const descQ = semNfDesc.trim().toLowerCase()
    const payQ = semNfPaymentType.trim().toLowerCase()
    return withoutNfList.filter((row) => {
      if (descQ && !(row.description ?? '').toLowerCase().includes(descQ)) return false
      if (payQ && !(row.payment_type ?? '').toLowerCase().includes(payQ)) return false
      if (semNfCategory && row.category_id !== semNfCategory) return false
      if (semNfCostCenter && row.cost_center_id !== semNfCostCenter) return false
      if (semNfDateFrom && (row.expense_date ?? '') < semNfDateFrom) return false
      if (semNfDateTo && (row.expense_date ?? '') > semNfDateTo) return false
      if (!matchesPaymentDatePeriod(row.payment_date, semNfPaymentDateFrom, semNfPaymentDateTo)) return false
      return true
    })
  }, [withoutNfList, semNfDesc, semNfPaymentType, semNfCategory, semNfCostCenter, semNfDateFrom, semNfDateTo, semNfPaymentDateFrom, semNfPaymentDateTo])

  const sortedSemNf = React.useMemo(() => {
    return [...filteredSemNf].sort((a, b) => {
      let av: string | number = ''
      let bv: string | number = ''
      if (semNfSort === 'expense_date') {
        av = a.expense_date ?? ''
        bv = b.expense_date ?? ''
      } else if (semNfSort === 'description') {
        av = (a.description ?? '').toLowerCase()
        bv = (b.description ?? '').toLowerCase()
      } else if (semNfSort === 'payment_type') {
        av = (a.payment_type ?? '').toLowerCase()
        bv = (b.payment_type ?? '').toLowerCase()
      } else if (semNfSort === 'installment') {
        av = (a.installment ?? '').toLowerCase()
        bv = (b.installment ?? '').toLowerCase()
      } else if (semNfSort === 'amount_paid') {
        av = a.amount_paid ?? 0
        bv = b.amount_paid ?? 0
      } else if (semNfSort === 'amount_to_pay') {
        av = a.amount_to_pay ?? 0
        bv = b.amount_to_pay ?? 0
      }
      if (av < bv) return semNfSortOrder === 'asc' ? -1 : 1
      if (av > bv) return semNfSortOrder === 'asc' ? 1 : -1
      return 0
    })
  }, [filteredSemNf, semNfSort, semNfSortOrder])

  const semNfPagesCount = Math.max(1, Math.ceil(sortedSemNf.length / PAGE_SIZE))
  const semNfStart = (semNfPage - 1) * PAGE_SIZE
  const paginatedSemNf = sortedSemNf.slice(semNfStart, semNfStart + PAGE_SIZE)

  const semNfTotals = React.useMemo(() => {
    const paid = filteredSemNf.reduce((s, r) => s + (r.amount_paid ?? 0), 0)
    const toPay = filteredSemNf.reduce((s, r) => s + (r.amount_to_pay ?? 0), 0)
    return { paid, toPay }
  }, [filteredSemNf])

  const nfeNotesTotalValue = React.useMemo(
    () => filteredNfeNotes.reduce((sum, nfe) => sum + (nfe.total_value ?? 0), 0),
    [filteredNfeNotes],
  )
  const nfeItemsUnitValueTotal = React.useMemo(
    () => filteredNfeItems.reduce((sum, item) => sum + (item.unit_value ?? 0), 0),
    [filteredNfeItems],
  )
  const nfeItemsQuantityTotal = React.useMemo(
    () => filteredNfeItems.reduce((sum, item) => sum + (item.quantity ?? 0), 0),
    [filteredNfeItems],
  )
  const nfeItemsTotalValue = React.useMemo(
    () => filteredNfeItems.reduce((sum, item) => sum + (item.total_value ?? 0), 0),
    [filteredNfeItems],
  )
  const nfseNotesTotalValue = React.useMemo(
    () => filteredNfseNotes.reduce((sum, nfse) => sum + (getNfseDisplayTotal(nfse) ?? 0), 0),
    [filteredNfseNotes],
  )
  const nfseItemsUnitValueTotal = React.useMemo(
    () => filteredNfseItems.reduce((sum, item) => sum + (item.unit_value ?? 0), 0),
    [filteredNfseItems],
  )
  const nfseItemsQuantityTotal = React.useMemo(
    () => filteredNfseItems.reduce((sum, item) => sum + (item.quantity ?? 0), 0),
    [filteredNfseItems],
  )
  const nfseItemsTotalValue = React.useMemo(
    () => filteredNfseItems.reduce((sum, item) => sum + (item.total_value ?? 0), 0),
    [filteredNfseItems],
  )
  const nfeItemsUnitValueAverage = React.useMemo(() => {
    const n = filteredNfeItems.length
    if (n === 0) return 0
    return filteredNfeItems.reduce((sum, item) => sum + (item.unit_value ?? 0), 0) / n
  }, [filteredNfeItems])
  const nfseItemsUnitValueAverage = React.useMemo(() => {
    const n = filteredNfseItems.length
    if (n === 0) return 0
    return filteredNfseItems.reduce((sum, item) => sum + (item.unit_value ?? 0), 0) / n
  }, [filteredNfseItems])

  const renderTotalsSummary = (
    totals: Array<{ label: string; value: number; kind?: 'currency' | 'quantity' }>,
  ) => (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
      <div className="flex flex-nowrap items-stretch gap-2 sm:gap-3 overflow-x-auto pb-0.5">
        {totals.map((item) => (
          <div
            key={item.label}
            className="flex min-w-[6.75rem] flex-1 flex-col rounded-lg border border-gray-200 dark:border-gray-700 px-2 sm:min-w-0 sm:px-3 py-2"
          >
            <Text size="xs" color="muted" className="uppercase tracking-wide">
              {item.label}
            </Text>
            <Text size="sm" className="font-semibold text-gray-900 dark:text-white mt-0.5 tabular-nums">
              {item.kind === 'quantity' ? formatQuantity(item.value) : formatCurrency(item.value)}
            </Text>
          </div>
        ))}
      </div>
    </div>
  )

  const renderPagination = (
    currentPage: number,
    pagesCount: number,
    total: number,
    setPage: React.Dispatch<React.SetStateAction<number>>,
  ) => {
    if (pagesCount <= 1) return null
    return (
      <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
        <Text size="sm" color="muted">
          Página {currentPage} de {pagesCount} ({total} {total === 1 ? 'registro' : 'registros'})
        </Text>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1}>
            Anterior
          </Button>
          <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(pagesCount, p + 1))} disabled={currentPage === pagesCount}>
            Próximo
          </Button>
        </div>
      </div>
    )
  }

  return (
    <>
      <main className="flex-1 flex flex-col overflow-x-hidden overflow-y-auto bg-gray-100 dark:bg-gray-900">
        <PermissionGuard
          moduleName="adm_despesas"
          permissionType="show"
          fallback={
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <Text size="xl" className="text-gray-500 dark:text-gray-400 mb-2">Acesso Negado</Text>
                <Text size="sm" color="muted">Você não tem permissão para acessar esta página.</Text>
              </div>
            </div>
          }
        >
          <div className="h-full flex flex-col">
            {/* Header */}
            <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex items-start justify-between gap-4">
              <div>
                <Heading as="h1" size="3xl" className="text-gray-900 dark:text-white">
                  {moduleInfo?.display_name || 'ADM • Despesas'}
                </Heading>
                <Text size="sm" color="muted" className="mt-1">
                  Em arquivos com baixa qualidade, dados ilegíveis ou situações similares, a importação por IA pode apresentar diferenças em relação ao original ou confundir campos no documento. Em caso de dúvidas, consulte o documento original.
                </Text>
                {moduleInfo?.description && (
                  <Text size="sm" color="muted" className="mt-1">
                    {moduleInfo.description}
                  </Text>
                )}
              </div>
              <Button variant="solid" size="md" onClick={openUploadModal} className="flex-shrink-0">
                <PlusIcon className="h-4 w-4" />
                Cadastrar Despesas
              </Button>
            </div>

            {/* Tabs */}
            <div className="px-6 border-b border-gray-200 dark:border-gray-700">
              <nav className="-mb-px flex space-x-8 overflow-x-auto">
                <button
                  type="button"
                  onClick={() => setActiveTab('all_documents')}
                  className={tabClass('all_documents')}
                >
                  <span className="inline-flex items-center gap-1.5">
                    Todos os Documentos
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('nfe')}
                  className={tabClass('nfe')}
                >
                  <span className="inline-flex items-center gap-1.5">
                    Compras NF-e
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('nfse')}
                  className={tabClass('nfse')}
                >
                  <span className="inline-flex items-center gap-1.5">
                    Serviços NFS-e
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('sem_nf')}
                  className={tabClass('sem_nf')}
                >
                  <span className="inline-flex items-center gap-1.5">
                    Planilha (sem NF)
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('contracts')}
                  className={tabClass('contracts')}
                >
                  <span className="inline-flex items-center gap-1.5">Contratos</span>
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('budgets')}
                  className={tabClass('budgets')}
                >
                  <span className="inline-flex items-center gap-1.5">Orçamentos/Pedidos</span>
                </button>
              </nav>
            </div>

            {/* Content */}
            {loading ? (
              <div className="flex-1 flex items-center justify-center">
                <LoadingSpinner />
              </div>
            ) : (
              <div className="p-6 space-y-4">
                {pageError && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
                    <Text size="sm">{pageError}</Text>
                  </div>
                )}

                {activeTab === 'all_documents' && (
                  <AdmDespesasAllDocumentsTab
                    onViewDocument={handleViewUnifiedDocument}
                    onEditDocument={handleEditUnifiedDocument}
                    refreshKey={documentsRefreshKey}
                    canEdit={canEdit}
                    canDelete={canDelete}
                    userId={user?.id}
                    onError={setPageError}
                    getErrorMessage={getErrorMessage}
                  />
                )}

                {/* ABA NF-e */}
                {activeTab === 'nfe' && (
                  <>
                    <Text size="sm" color="muted">
                      Lista e organiza despesas de compras (NF-e), com filtros, detalhamento e impressão.
                    </Text>
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                      <div className="mb-3">
                        <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">Filtros</Heading>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        <Input type="text" placeholder="Emitente" value={nfeItemsIssuer} onChange={(e) => { setNfeItemsIssuer(e.target.value); setNfeItemsPage(1); setNfeNotesPage(1) }} />
                        <Input type="text" placeholder="Número da nota" value={nfeItemsNoteNumber} onChange={(e) => { setNfeItemsNoteNumber(e.target.value); setNfeItemsPage(1); setNfeNotesPage(1) }} />
                        <Input type="text" placeholder="Chave de acesso" value={nfeItemsAccessKey} onChange={(e) => { setNfeItemsAccessKey(e.target.value); setNfeItemsPage(1); setNfeNotesPage(1) }} />
                        <Input
                          type="text"
                          placeholder={FILTRO_NOME_ARQUIVO}
                          title={FILTRO_NOME_ARQUIVO_TITLE}
                          value={nfeItemsFileName}
                          onChange={(e) => {
                            setNfeItemsFileName(e.target.value)
                            setNfeItemsPage(1)
                            setNfeNotesPage(1)
                          }}
                        />
                        <Input
                          type="text"
                          placeholder="Descrição (itens)"
                          title="Filtra pela descrição dos itens da nota (ambas as subabas)"
                          value={nfeItemsSearch}
                          onChange={(e) => {
                            setNfeItemsSearch(e.target.value)
                            setNfeItemsPage(1)
                            setNfeNotesPage(1)
                          }}
                        />
                        <select value={nfeItemsCategory} onChange={(e) => { setNfeItemsCategory(e.target.value); setNfeItemsPage(1); setNfeNotesPage(1) }} className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-white/5 px-3 py-2 text-sm text-gray-900 dark:text-white shadow-sm">
                          <option value="">Categoria: Todas</option>
                          {categorias.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                        <select value={nfeItemsCostCenter} onChange={(e) => { setNfeItemsCostCenter(e.target.value); setNfeItemsPage(1); setNfeNotesPage(1) }} className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-white/5 px-3 py-2 text-sm text-gray-900 dark:text-white shadow-sm">
                          <option value="">Centro de custo: Todos</option>
                          {centrosCusto.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                        <PaymentDatePeriodFilterFields
                          from={nfePaymentDateFrom}
                          to={nfePaymentDateTo}
                          onFromChange={setNfePaymentDateFrom}
                          onToChange={setNfePaymentDateTo}
                          onChange={() => { setNfeItemsPage(1); setNfeNotesPage(1) }}
                        />
                        <div className="col-span-full flex items-center justify-end gap-2">
                          <Button variant="outline" size="sm" onClick={() => { setNfeItemsSearch(''); setNfeItemsIssuer(''); setNfeItemsNoteNumber(''); setNfeItemsAccessKey(''); setNfeItemsFileName(''); setNfeItemsCategory(''); setNfeItemsCostCenter(''); setNfeNotesSearch(''); setNfeNotesCnpj(''); setNfeNotesNumber(''); setNfeNotesAccessKey(''); setNfeNotesFileName(''); setNfePaymentDateFrom(''); setNfePaymentDateTo(''); setNfeItemsPage(1); setNfeNotesPage(1) }}>
                            Limpar filtros
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => openPrintDialog(nfeSubTab === 'notes' ? 'nfe_notes' : 'nfe_items')}>
                            <ArrowDownTrayIcon className="h-4 w-4" />
                            Imprimir
                          </Button>
                        </div>
                      </div>
                    </div>
                    {/* Subabas NF-e */}
                    <div className="border-b border-gray-200 dark:border-gray-700">
                      <nav className="flex space-x-6">
                        <button type="button" onClick={() => setNfeSubTab('notes')} className={subTabClass(nfeSubTab === 'notes')}>
                          <span className="inline-flex items-center gap-1.5">
                            Notas Fiscais
                            <InfoTip content="Relação das notas NF-e com dados de emissão, emitente e valores totais." />
                          </span>
                        </button>
                        <button type="button" onClick={() => setNfeSubTab('items')} className={subTabClass(nfeSubTab === 'items')}>
                          <span className="inline-flex items-center gap-1.5">
                            Itens
                            <InfoTip content="Relação item a item das NF-e para análise por descrição, categoria e centro de custo." />
                          </span>
                          {filteredNfeItems.length > 0 && (
                            <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                              {filteredNfeItems.length}
                            </span>
                          )}
                        </button>
                      </nav>
                    </div>

                    {/* Subaba Notas NF-e */}
                    {nfeSubTab === 'notes' && (
                      <>
                        {renderTotalsSummary([
                          { label: 'Soma Valor Total', value: nfeNotesTotalValue },
                        ])}
                        {/* Tabela */}
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-x-auto">
                          {paginatedNfeNotes.length === 0 ? (
                            <div className="px-4 py-12 text-center">
                              <DocumentIcon className="mx-auto h-10 w-10 text-gray-300 dark:text-gray-600 mb-3" />
                              <Text size="sm" color="muted">{nfeList.length === 0 ? 'Nenhuma NF-e cadastrada.' : 'Nenhuma nota encontrada com os filtros aplicados.'}</Text>
                            </div>
                          ) : (
                            <>
                              <table className="w-full min-w-[800px] divide-y divide-gray-200 dark:divide-gray-700">
                                <thead className="bg-gray-50 dark:bg-gray-700">
                                  <tr>
                                    <SortableTableHeader sortKey="number" currentSort={nfeNotesSort} sortOrder={nfeNotesSortOrder} onSort={toggleSort(nfeNotesSort, setNfeNotesSort, nfeNotesSortOrder, setNfeNotesSortOrder, setNfeNotesPage)} className="px-4 py-3">Número</SortableTableHeader>
                                    <SortableTableHeader sortKey="issuer_name" currentSort={nfeNotesSort} sortOrder={nfeNotesSortOrder} onSort={toggleSort(nfeNotesSort, setNfeNotesSort, nfeNotesSortOrder, setNfeNotesSortOrder, setNfeNotesPage)} className="px-4 py-3">Emitente</SortableTableHeader>
                                    <SortableTableHeader sortKey="issue_date" currentSort={nfeNotesSort} sortOrder={nfeNotesSortOrder} onSort={toggleSort(nfeNotesSort, setNfeNotesSort, nfeNotesSortOrder, setNfeNotesSortOrder, setNfeNotesPage)} className="px-4 py-3">Emissão</SortableTableHeader>
                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Nº Itens</th>
                                    <SortableTableHeader sortKey="total_value" currentSort={nfeNotesSort} sortOrder={nfeNotesSortOrder} onSort={toggleSort(nfeNotesSort, setNfeNotesSort, nfeNotesSortOrder, setNfeNotesSortOrder, setNfeNotesPage)} className="px-4 py-3 text-right">Valor Total</SortableTableHeader>
                                    {(canDelete || canEdit) && <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Ações</th>}
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                  {paginatedNfeNotes.map((nfe) => (
                                    <tr key={nfe.id} onClick={() => void openNfeDetail(nfe)} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors cursor-pointer">
                                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white whitespace-nowrap">
                                        {nfe.number || '—'}
                                      </td>
                                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white max-w-[320px] truncate" title={nfe.issuer_name ?? ''}>{nfe.issuer_name ? (nfe.issuer_name.length > 40 ? nfe.issuer_name.slice(0, 40) + '…' : nfe.issuer_name) : '—'}</td>
                                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">{formatDate(nfe.issue_date)}</td>
                                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white text-right whitespace-nowrap">{nfeItemsCountByHeader.get(nfe.id) ?? 0}</td>
                                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white text-right whitespace-nowrap font-medium">{formatCurrency(nfe.total_value)}</td>
                                      {(canDelete || canEdit) && (
                                        <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                                          <div className="flex items-center justify-center gap-2">
                                            {canEdit && (
                                              <button
                                                type="button"
                                                title="Editar cabeçalho"
                                                onClick={() => openHeaderEditModal('nfe', nfe)}
                                                className="text-primary-500 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
                                              >
                                                <PencilIcon className="h-4 w-4" />
                                              </button>
                                            )}
                                            {canDelete && (
                                              <button type="button" title="Excluir" onClick={() => openDeleteModal('nfe', nfe.id, `NF-e ${nfe.number || nfe.id}`)} className="text-red-400 hover:text-red-600 dark:hover:text-red-300 transition-colors">
                                                <TrashIcon className="h-4 w-4" />
                                              </button>
                                            )}
                                          </div>
                                        </td>
                                      )}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                              {renderPagination(nfeNotesPage, nfeNotesPagesCount, filteredNfeNotes.length, setNfeNotesPage)}
                            </>
                          )}
                        </div>
                      </>
                    )}

                    {/* Subaba Itens NF-e */}
                    {nfeSubTab === 'items' && (
                      <>
                        {renderTotalsSummary([
                          { label: 'Soma Qtd', value: nfeItemsQuantityTotal, kind: 'quantity' },
                          { label: 'Soma Vl. Unit.', value: nfeItemsUnitValueTotal },
                          { label: 'Média Vl. Unit.', value: nfeItemsUnitValueAverage },
                          { label: 'Soma Total', value: nfeItemsTotalValue },
                        ])}
                        {/* Tabela */}
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-x-auto">
                          {paginatedNfeItems.length === 0 ? (
                            <div className="px-4 py-12 text-center">
                              <DocumentIcon className="mx-auto h-10 w-10 text-gray-300 dark:text-gray-600 mb-3" />
                              <Text size="sm" color="muted">{allNfeItems.length === 0 ? 'Nenhum item cadastrado.' : 'Nenhum item encontrado com os filtros aplicados.'}</Text>
                            </div>
                          ) : (
                            <>
                              <table className="w-full min-w-[900px] divide-y divide-gray-200 dark:divide-gray-700">
                                <thead className="bg-gray-50 dark:bg-gray-700">
                                  <tr>
                                    <SortableTableHeader sortKey="number" currentSort={nfeItemsSort} sortOrder={nfeItemsSortOrder} onSort={toggleSort(nfeItemsSort, setNfeItemsSort, nfeItemsSortOrder, setNfeItemsSortOrder, setNfeItemsPage)} className="px-4 py-3">Nota</SortableTableHeader>
                                    <SortableTableHeader sortKey="issuer_name" currentSort={nfeItemsSort} sortOrder={nfeItemsSortOrder} onSort={toggleSort(nfeItemsSort, setNfeItemsSort, nfeItemsSortOrder, setNfeItemsSortOrder, setNfeItemsPage)} className="px-4 py-3">Emitente</SortableTableHeader>
                                    <SortableTableHeader sortKey="description" currentSort={nfeItemsSort} sortOrder={nfeItemsSortOrder} onSort={toggleSort(nfeItemsSort, setNfeItemsSort, nfeItemsSortOrder, setNfeItemsSortOrder, setNfeItemsPage)} className="px-4 py-3">Descrição</SortableTableHeader>
                                    <SortableTableHeader sortKey="quantity" currentSort={nfeItemsSort} sortOrder={nfeItemsSortOrder} onSort={toggleSort(nfeItemsSort, setNfeItemsSort, nfeItemsSortOrder, setNfeItemsSortOrder, setNfeItemsPage)} className="px-4 py-3 text-right">Qtd</SortableTableHeader>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Un</th>
                                    <SortableTableHeader sortKey="unit_value" currentSort={nfeItemsSort} sortOrder={nfeItemsSortOrder} onSort={toggleSort(nfeItemsSort, setNfeItemsSort, nfeItemsSortOrder, setNfeItemsSortOrder, setNfeItemsPage)} className="px-4 py-3 text-right">Vl. Unit.</SortableTableHeader>
                                    <SortableTableHeader sortKey="total_value" currentSort={nfeItemsSort} sortOrder={nfeItemsSortOrder} onSort={toggleSort(nfeItemsSort, setNfeItemsSort, nfeItemsSortOrder, setNfeItemsSortOrder, setNfeItemsPage)} className="px-4 py-3 text-right">Total</SortableTableHeader>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Categoria</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Centro de Custo</th>
                                    {(canDelete || canEdit) && (
                                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Ações</th>
                                    )}
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                  {paginatedNfeItems.map((item) => {
                                    const header = nfeHeaderMap.get(item.nfe_id)
                                    return (
                                      <tr key={item.id} onClick={() => header && void openNfeDetail(header)} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors cursor-pointer">
                                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white whitespace-nowrap font-medium">{header?.number || '—'}</td>
                                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 max-w-[160px] truncate" title={header?.issuer_name ?? ''}>{header?.issuer_name || '—'}</td>
                                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white max-w-[220px] truncate" title={item.description ?? ''}>{item.description || '—'}</td>
                                        <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-white">{item.quantity ?? '—'}</td>
                                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{item.unit || '—'}</td>
                                        <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-white">{formatCurrency(item.unit_value)}</td>
                                        <td className="px-4 py-3 text-sm text-right font-medium text-gray-900 dark:text-white">{formatCurrency(item.total_value)}</td>
                                        <td className="px-4 py-3 min-w-[160px]">
                                          {item.category_id ? (
                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">
                                              {categoryMap.get(item.category_id) ?? item.category_id}
                                            </span>
                                          ) : (
                                            <span className="text-gray-400 dark:text-gray-500 text-xs">—</span>
                                          )}
                                        </td>
                                        <td className="px-4 py-3 min-w-[160px]">
                                          {item.cost_center_id ? (
                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
                                              {costCenterMap.get(item.cost_center_id) ?? item.cost_center_id}
                                            </span>
                                          ) : (
                                            <span className="text-gray-400 dark:text-gray-500 text-xs">—</span>
                                          )}
                                        </td>
                                        {(canDelete || canEdit) && (
                                          <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                                            <div className="flex items-center justify-center gap-2">
                                              {canEdit && (
                                                <button
                                                  type="button"
                                                  title="Editar item"
                                                  onClick={() => openItemEditModal('nfe', item)}
                                                  className="text-primary-500 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
                                                >
                                                  <PencilIcon className="h-4 w-4" />
                                                </button>
                                              )}
                                              {canDelete && (
                                                <button
                                                  type="button"
                                                  title="Excluir item"
                                                  onClick={() => void handleSoftDeleteItem('nfe', item)}
                                                  disabled={savingItem === item.id}
                                                  className="text-red-400 hover:text-red-600 dark:hover:text-red-300 transition-colors disabled:opacity-50"
                                                >
                                                  <TrashIcon className="h-4 w-4" />
                                                </button>
                                              )}
                                            </div>
                                          </td>
                                        )}
                                      </tr>
                                    )
                                  })}
                                </tbody>
                              </table>
                              {renderPagination(nfeItemsPage, nfeItemsPagesCount, filteredNfeItems.length, setNfeItemsPage)}
                            </>
                          )}
                        </div>
                      </>
                    )}
                  </>
                )}

                {/* ABA NFS-e */}
                {activeTab === 'nfse' && (
                  <>
                    <Text size="sm" color="muted">
                      Lista e organiza despesas de serviços (NFS-e), com filtros, detalhamento e impressão.
                    </Text>
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                      <div className="mb-3">
                        <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">Filtros</Heading>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        <Input type="text" placeholder="Prestador" value={nfseItemsProvider} onChange={(e) => { setNfseItemsProvider(e.target.value); setNfseItemsPage(1); setNfseNotesPage(1) }} />
                        <Input type="text" placeholder="Número da nota" value={nfseItemsNoteNumber} onChange={(e) => { setNfseItemsNoteNumber(e.target.value); setNfseItemsPage(1); setNfseNotesPage(1) }} />
                        <Input type="text" placeholder="Chave de acesso" value={nfseItemsAccessKey} onChange={(e) => { setNfseItemsAccessKey(e.target.value); setNfseItemsPage(1); setNfseNotesPage(1) }} />
                        <Input
                          type="text"
                          placeholder={FILTRO_NOME_ARQUIVO}
                          title={FILTRO_NOME_ARQUIVO_TITLE}
                          value={nfseItemsFileName}
                          onChange={(e) => {
                            setNfseItemsFileName(e.target.value)
                            setNfseItemsPage(1)
                            setNfseNotesPage(1)
                          }}
                        />
                        <Input
                          type="text"
                          placeholder="Descrição (serviços)"
                          title="Filtra pela descrição dos serviços da NFS-e (ambas as subabas)"
                          value={nfseItemsSearch}
                          onChange={(e) => {
                            setNfseItemsSearch(e.target.value)
                            setNfseItemsPage(1)
                            setNfseNotesPage(1)
                          }}
                        />
                        <Input
                          type="text"
                          placeholder="Código do serviço"
                          title="Filtra pelo código do serviço nos itens (ambas as subabas)"
                          value={nfseItemsServiceCode}
                          onChange={(e) => {
                            setNfseItemsServiceCode(e.target.value)
                            setNfseItemsPage(1)
                            setNfseNotesPage(1)
                          }}
                        />
                        <select value={nfseItemsCategory} onChange={(e) => { setNfseItemsCategory(e.target.value); setNfseItemsPage(1); setNfseNotesPage(1) }} className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-white/5 px-3 py-2 text-sm text-gray-900 dark:text-white shadow-sm">
                          <option value="">Categoria: Todas</option>
                          {categorias.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                        <select value={nfseItemsCostCenter} onChange={(e) => { setNfseItemsCostCenter(e.target.value); setNfseItemsPage(1); setNfseNotesPage(1) }} className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-white/5 px-3 py-2 text-sm text-gray-900 dark:text-white shadow-sm">
                          <option value="">Centro de custo: Todos</option>
                          {centrosCusto.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                        <PaymentDatePeriodFilterFields
                          from={nfsePaymentDateFrom}
                          to={nfsePaymentDateTo}
                          onFromChange={setNfsePaymentDateFrom}
                          onToChange={setNfsePaymentDateTo}
                          onChange={() => { setNfseItemsPage(1); setNfseNotesPage(1) }}
                        />
                        <div className="col-span-full flex items-center justify-end gap-2">
                          <Button variant="outline" size="sm" onClick={() => { setNfseItemsSearch(''); setNfseItemsProvider(''); setNfseItemsNoteNumber(''); setNfseItemsAccessKey(''); setNfseItemsFileName(''); setNfseItemsServiceCode(''); setNfseItemsCategory(''); setNfseItemsCostCenter(''); setNfseNotesSearch(''); setNfseNotesCnpj(''); setNfseNotesNumber(''); setNfseNotesAccessKey(''); setNfseNotesFileName(''); setNfsePaymentDateFrom(''); setNfsePaymentDateTo(''); setNfseItemsPage(1); setNfseNotesPage(1) }}>
                            Limpar filtros
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => openPrintDialog(nfseSubTab === 'notes' ? 'nfse_notes' : 'nfse_items')}>
                            <ArrowDownTrayIcon className="h-4 w-4" />
                            Imprimir
                          </Button>
                        </div>
                      </div>
                    </div>
                    {/* Subabas NFS-e */}
                    <div className="border-b border-gray-200 dark:border-gray-700">
                      <nav className="flex space-x-6">
                        <button type="button" onClick={() => setNfseSubTab('notes')} className={subTabClass(nfseSubTab === 'notes')}>
                          <span className="inline-flex items-center gap-1.5">
                            Notas Fiscais
                            <InfoTip content="Relação das notas NFS-e com competência, prestador e valor total considerado nos cálculos." />
                          </span>
                        </button>
                        <button type="button" onClick={() => setNfseSubTab('items')} className={subTabClass(nfseSubTab === 'items')}>
                          <span className="inline-flex items-center gap-1.5">
                            Serviços
                            <InfoTip content="Relação detalhada dos serviços das NFS-e para análise por categoria e centro de custo." />
                          </span>
                          {filteredNfseItems.length > 0 && (
                            <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                              {filteredNfseItems.length}
                            </span>
                          )}
                        </button>
                      </nav>
                    </div>

                    {/* Subaba Notas NFS-e */}
                    {nfseSubTab === 'notes' && (
                      <>
                        {renderTotalsSummary([
                          { label: 'Soma Valor Total', value: nfseNotesTotalValue },
                        ])}
                        {/* Tabela */}
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-x-auto">
                          {paginatedNfseNotes.length === 0 ? (
                            <div className="px-4 py-12 text-center">
                              <DocumentIcon className="mx-auto h-10 w-10 text-gray-300 dark:text-gray-600 mb-3" />
                              <Text size="sm" color="muted">{nfseList.length === 0 ? 'Nenhuma NFS-e cadastrada.' : 'Nenhuma nota encontrada com os filtros aplicados.'}</Text>
                            </div>
                          ) : (
                            <>
                              <table className="w-full min-w-[800px] divide-y divide-gray-200 dark:divide-gray-700">
                                <thead className="bg-gray-50 dark:bg-gray-700">
                                  <tr>
                                    <SortableTableHeader sortKey="number" currentSort={nfseNotesSort} sortOrder={nfseNotesSortOrder} onSort={toggleSort(nfseNotesSort, setNfseNotesSort, nfseNotesSortOrder, setNfseNotesSortOrder, setNfseNotesPage)} className="px-4 py-3">Número</SortableTableHeader>
                                    <SortableTableHeader sortKey="provider_name" currentSort={nfseNotesSort} sortOrder={nfseNotesSortOrder} onSort={toggleSort(nfseNotesSort, setNfseNotesSort, nfseNotesSortOrder, setNfseNotesSortOrder, setNfseNotesPage)} className="px-4 py-3">Prestador</SortableTableHeader>
                                    <SortableTableHeader sortKey="issue_date" currentSort={nfseNotesSort} sortOrder={nfseNotesSortOrder} onSort={toggleSort(nfseNotesSort, setNfseNotesSort, nfseNotesSortOrder, setNfseNotesSortOrder, setNfseNotesPage)} className="px-4 py-3">Competência</SortableTableHeader>
                                    <SortableTableHeader sortKey="net_value" currentSort={nfseNotesSort} sortOrder={nfseNotesSortOrder} onSort={toggleSort(nfseNotesSort, setNfseNotesSort, nfseNotesSortOrder, setNfseNotesSortOrder, setNfseNotesPage)} className="px-4 py-3 text-right">Valor Total</SortableTableHeader>
                                    {(canDelete || canEdit) && <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Ações</th>}
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                  {paginatedNfseNotes.map((nfse) => (
                                    <tr key={nfse.id} onClick={() => void openNfseDetail(nfse)} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors cursor-pointer">
                                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white whitespace-nowrap">{nfse.number || '—'}</td>
                                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white max-w-[320px] truncate" title={nfse.provider_name ?? ''}>{nfse.provider_name ? (nfse.provider_name.length > 40 ? nfse.provider_name.slice(0, 40) + '…' : nfse.provider_name) : '—'}</td>
                                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
                                        {nfse.competence_date ? new Date(nfse.competence_date + 'T00:00:00').toLocaleDateString('pt-BR', { month: '2-digit', year: 'numeric' }) : '—'}
                                      </td>
                                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white text-right whitespace-nowrap font-medium">{formatCurrency(getNfseDisplayTotal(nfse))}</td>
                                      {(canDelete || canEdit) && (
                                        <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                                          <div className="flex items-center justify-center gap-2">
                                            {canEdit && (
                                              <button
                                                type="button"
                                                title="Editar cabeçalho"
                                                onClick={() => openHeaderEditModal('nfse', nfse)}
                                                className="text-primary-500 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
                                              >
                                                <PencilIcon className="h-4 w-4" />
                                              </button>
                                            )}
                                            {canDelete && (
                                              <button type="button" title="Excluir" onClick={() => openDeleteModal('nfse', nfse.id, `NFS-e ${nfse.number || nfse.id}`)} className="text-red-400 hover:text-red-600 dark:hover:text-red-300 transition-colors">
                                                <TrashIcon className="h-4 w-4" />
                                              </button>
                                            )}
                                          </div>
                                        </td>
                                      )}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                              {renderPagination(nfseNotesPage, nfseNotesPagesCount, filteredNfseNotes.length, setNfseNotesPage)}
                            </>
                          )}
                        </div>
                      </>
                    )}

                    {/* Subaba Itens NFS-e */}
                    {nfseSubTab === 'items' && (
                      <>
                        {renderTotalsSummary([
                          { label: 'Soma Qtd', value: nfseItemsQuantityTotal, kind: 'quantity' },
                          { label: 'Soma Vl. Unit.', value: nfseItemsUnitValueTotal },
                          { label: 'Média Vl. Unit.', value: nfseItemsUnitValueAverage },
                          { label: 'Soma Total', value: nfseItemsTotalValue },
                        ])}
                        {/* Tabela */}
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-x-auto">
                          {paginatedNfseItems.length === 0 ? (
                            <div className="px-4 py-12 text-center">
                              <DocumentIcon className="mx-auto h-10 w-10 text-gray-300 dark:text-gray-600 mb-3" />
                              <Text size="sm" color="muted">{allNfseItems.length === 0 ? 'Nenhum item cadastrado.' : 'Nenhum item encontrado com os filtros aplicados.'}</Text>
                            </div>
                          ) : (
                            <>
                              <table className="w-full min-w-[900px] divide-y divide-gray-200 dark:divide-gray-700">
                                <thead className="bg-gray-50 dark:bg-gray-700">
                                  <tr>
                                    <SortableTableHeader sortKey="number" currentSort={nfseItemsSort} sortOrder={nfseItemsSortOrder} onSort={toggleSort(nfseItemsSort, setNfseItemsSort, nfseItemsSortOrder, setNfseItemsSortOrder, setNfseItemsPage)} className="px-4 py-3">Nota</SortableTableHeader>
                                    <SortableTableHeader sortKey="provider_name" currentSort={nfseItemsSort} sortOrder={nfseItemsSortOrder} onSort={toggleSort(nfseItemsSort, setNfseItemsSort, nfseItemsSortOrder, setNfseItemsSortOrder, setNfseItemsPage)} className="px-4 py-3">Prestador</SortableTableHeader>
                                    <SortableTableHeader sortKey="description" currentSort={nfseItemsSort} sortOrder={nfseItemsSortOrder} onSort={toggleSort(nfseItemsSort, setNfseItemsSort, nfseItemsSortOrder, setNfseItemsSortOrder, setNfseItemsPage)} className="px-4 py-3">Detalhes do Serviço</SortableTableHeader>
                                    <SortableTableHeader sortKey="quantity" currentSort={nfseItemsSort} sortOrder={nfseItemsSortOrder} onSort={toggleSort(nfseItemsSort, setNfseItemsSort, nfseItemsSortOrder, setNfseItemsSortOrder, setNfseItemsPage)} className="px-4 py-3 text-right">Qtd</SortableTableHeader>
                                    <SortableTableHeader sortKey="unit_value" currentSort={nfseItemsSort} sortOrder={nfseItemsSortOrder} onSort={toggleSort(nfseItemsSort, setNfseItemsSort, nfseItemsSortOrder, setNfseItemsSortOrder, setNfseItemsPage)} className="px-4 py-3 text-right">Vl. Unit.</SortableTableHeader>
                                    <SortableTableHeader sortKey="total_value" currentSort={nfseItemsSort} sortOrder={nfseItemsSortOrder} onSort={toggleSort(nfseItemsSort, setNfseItemsSort, nfseItemsSortOrder, setNfseItemsSortOrder, setNfseItemsPage)} className="px-4 py-3 text-right">Total</SortableTableHeader>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Categoria</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Centro de Custo</th>
                                    {(canDelete || canEdit) && (
                                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Ações</th>
                                    )}
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                  {paginatedNfseItems.map((item) => {
                                    const header = nfseHeaderMap.get(item.nfse_id)
                                    return (
                                      <tr key={item.id} onClick={() => header && void openNfseDetail(header)} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors cursor-pointer">
                                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white whitespace-nowrap font-medium">{header?.number || '—'}</td>
                                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 max-w-[160px] truncate" title={header?.provider_name ?? ''}>{header?.provider_name || '—'}</td>
                                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white max-w-[220px] truncate" title={item.description ?? ''}>{item.description || '—'}</td>
                                        <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-white">{item.quantity ?? '—'}</td>
                                        <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-white">{formatCurrency(item.unit_value)}</td>
                                        <td className="px-4 py-3 text-sm text-right font-medium text-gray-900 dark:text-white">{formatCurrency(item.total_value)}</td>
                                        <td className="px-4 py-3 min-w-[160px]">
                                          {item.category_id ? (
                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">
                                              {categoryMap.get(item.category_id) ?? item.category_id}
                                            </span>
                                          ) : (
                                            <span className="text-gray-400 dark:text-gray-500 text-xs">—</span>
                                          )}
                                        </td>
                                        <td className="px-4 py-3 min-w-[160px]">
                                          {item.cost_center_id ? (
                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
                                              {costCenterMap.get(item.cost_center_id) ?? item.cost_center_id}
                                            </span>
                                          ) : (
                                            <span className="text-gray-400 dark:text-gray-500 text-xs">—</span>
                                          )}
                                        </td>
                                        {(canDelete || canEdit) && (
                                          <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                                            <div className="flex items-center justify-center gap-2">
                                              {canEdit && (
                                                <button
                                                  type="button"
                                                  title="Editar item"
                                                  onClick={() => openItemEditModal('nfse', item)}
                                                  className="text-primary-500 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
                                                >
                                                  <PencilIcon className="h-4 w-4" />
                                                </button>
                                              )}
                                              {canDelete && (
                                                <button
                                                  type="button"
                                                  title="Excluir item"
                                                  onClick={() => void handleSoftDeleteItem('nfse', item)}
                                                  disabled={savingItem === item.id}
                                                  className="text-red-400 hover:text-red-600 dark:hover:text-red-300 transition-colors disabled:opacity-50"
                                                >
                                                  <TrashIcon className="h-4 w-4" />
                                                </button>
                                              )}
                                            </div>
                                          </td>
                                        )}
                                      </tr>
                                    )
                                  })}
                                </tbody>
                              </table>
                              {renderPagination(nfseItemsPage, nfseItemsPagesCount, filteredNfseItems.length, setNfseItemsPage)}
                            </>
                          )}
                        </div>
                      </>
                    )}
                  </>
                )}

                {activeTab === 'sem_nf' && (
                  <>
                    <Text size="sm" color="muted">
                      Registros importados da planilha de pagamentos (sem nota fiscal), com filtros e totais.
                    </Text>
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                      <div className="mb-3">
                        <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">Filtros</Heading>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        <Input type="text" placeholder="Descrição" value={semNfDesc} onChange={(e) => { setSemNfDesc(e.target.value); setSemNfPage(1) }} />
                        <Input type="text" placeholder="Tipo de pagamento" value={semNfPaymentType} onChange={(e) => { setSemNfPaymentType(e.target.value); setSemNfPage(1) }} />
                        <select value={semNfCategory} onChange={(e) => { setSemNfCategory(e.target.value); setSemNfPage(1) }} className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-white/5 px-3 py-2 text-sm text-gray-900 dark:text-white shadow-sm">
                          <option value="">Categoria: Todas</option>
                          {categorias.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                        <select value={semNfCostCenter} onChange={(e) => { setSemNfCostCenter(e.target.value); setSemNfPage(1) }} className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-white/5 px-3 py-2 text-sm text-gray-900 dark:text-white shadow-sm">
                          <option value="">Centro de custo: Todos</option>
                          {centrosCusto.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                        <div
                          className="flex min-w-0 items-center gap-2"
                          title="Data inicial da despesa na planilha (expense_date, inclusiva)"
                        >
                          <span className="shrink-0 text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">
                            Despesa de
                          </span>
                          <Input
                            type="date"
                            className="min-w-0 flex-1"
                            aria-label="Despesa de"
                            value={semNfDateFrom}
                            onChange={(e) => {
                              setSemNfDateFrom(e.target.value)
                              setSemNfPage(1)
                            }}
                          />
                        </div>
                        <div
                          className="flex min-w-0 items-center gap-2"
                          title="Data final da despesa na planilha (expense_date, inclusiva)"
                        >
                          <span className="shrink-0 text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">
                            Despesa até
                          </span>
                          <Input
                            type="date"
                            className="min-w-0 flex-1"
                            aria-label="Despesa até"
                            value={semNfDateTo}
                            onChange={(e) => {
                              setSemNfDateTo(e.target.value)
                              setSemNfPage(1)
                            }}
                          />
                        </div>
                        <PaymentDatePeriodFilterFields
                          from={semNfPaymentDateFrom}
                          to={semNfPaymentDateTo}
                          onFromChange={setSemNfPaymentDateFrom}
                          onToChange={setSemNfPaymentDateTo}
                          onChange={() => setSemNfPage(1)}
                        />
                        <div className="col-span-full flex items-center justify-end gap-2">
                          <Button variant="outline" size="sm" onClick={() => { setSemNfDesc(''); setSemNfPaymentType(''); setSemNfCategory(''); setSemNfCostCenter(''); setSemNfDateFrom(''); setSemNfDateTo(''); setSemNfPaymentDateFrom(''); setSemNfPaymentDateTo(''); setSemNfPage(1) }}>
                            Limpar filtros
                          </Button>
                        </div>
                      </div>
                    </div>
                    {renderTotalsSummary([
                      { label: 'Soma Valor Pago', value: semNfTotals.paid },
                      { label: 'Soma Valor a Pagar', value: semNfTotals.toPay },
                    ])}
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-x-auto">
                      {paginatedSemNf.length === 0 ? (
                        <div className="px-4 py-12 text-center">
                          <DocumentIcon className="mx-auto h-10 w-10 text-gray-300 dark:text-gray-600 mb-3" />
                          <Text size="sm" color="muted">{withoutNfList.length === 0 ? 'Nenhum registro importado.' : 'Nenhum registro encontrado com os filtros aplicados.'}</Text>
                        </div>
                      ) : (
                        <>
                          <table className="w-full min-w-[960px] divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gray-50 dark:bg-gray-700">
                              <tr>
                                <SortableTableHeader sortKey="payment_type" currentSort={semNfSort} sortOrder={semNfSortOrder} onSort={toggleSort(semNfSort, setSemNfSort, semNfSortOrder, setSemNfSortOrder, setSemNfPage)} className="px-4 py-3">Tipo pgto</SortableTableHeader>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Categoria</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Centro de Custo</th>
                                <SortableTableHeader sortKey="expense_date" currentSort={semNfSort} sortOrder={semNfSortOrder} onSort={toggleSort(semNfSort, setSemNfSort, semNfSortOrder, setSemNfSortOrder, setSemNfPage)} className="px-4 py-3">Data</SortableTableHeader>
                                <SortableTableHeader sortKey="description" currentSort={semNfSort} sortOrder={semNfSortOrder} onSort={toggleSort(semNfSort, setSemNfSort, semNfSortOrder, setSemNfSortOrder, setSemNfPage)} className="px-4 py-3">Descrição</SortableTableHeader>
                                <SortableTableHeader sortKey="installment" currentSort={semNfSort} sortOrder={semNfSortOrder} onSort={toggleSort(semNfSort, setSemNfSort, semNfSortOrder, setSemNfSortOrder, setSemNfPage)} className="px-4 py-3">Parcela</SortableTableHeader>
                                <SortableTableHeader sortKey="amount_paid" currentSort={semNfSort} sortOrder={semNfSortOrder} onSort={toggleSort(semNfSort, setSemNfSort, semNfSortOrder, setSemNfSortOrder, setSemNfPage)} className="px-4 py-3 text-right">Valor pago</SortableTableHeader>
                                <SortableTableHeader sortKey="amount_to_pay" currentSort={semNfSort} sortOrder={semNfSortOrder} onSort={toggleSort(semNfSort, setSemNfSort, semNfSortOrder, setSemNfSortOrder, setSemNfPage)} className="px-4 py-3 text-right">Valor a pagar</SortableTableHeader>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Obs.</th>
                                {(canDelete || canEdit) && (
                                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Ações</th>
                                )}
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                              {paginatedSemNf.map((row) => (
                                <tr
                                  key={row.id}
                                  onClick={() => setWithoutNfDetailModal(row)}
                                  className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors cursor-pointer"
                                >
                                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-white whitespace-nowrap">{row.payment_type || '—'}</td>
                                  <td className="px-4 py-3 min-w-[160px]">
                                    {row.category_id ? (
                                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">
                                        {categoryMap.get(row.category_id) ?? row.category_id}
                                      </span>
                                    ) : (
                                      <span className="text-gray-400 dark:text-gray-500 text-xs">—</span>
                                    )}
                                  </td>
                                  <td className="px-4 py-3 min-w-[160px]">
                                    {row.cost_center_id ? (
                                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
                                        {costCenterMap.get(row.cost_center_id) ?? row.cost_center_id}
                                      </span>
                                    ) : (
                                      <span className="text-gray-400 dark:text-gray-500 text-xs">—</span>
                                    )}
                                  </td>
                                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">{formatDate(row.expense_date)}</td>
                                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-white max-w-[200px] truncate" title={row.description ?? ''}>{row.description || '—'}</td>
                                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-white whitespace-nowrap">{row.installment || '—'}</td>
                                  <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-white whitespace-nowrap">{formatCurrency(row.amount_paid)}</td>
                                  <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-white whitespace-nowrap">{formatCurrency(row.amount_to_pay)}</td>
                                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 max-w-[180px] truncate" title={row.notes ?? ''}>{row.notes || '—'}</td>
                                  {(canDelete || canEdit) && (
                                    <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                                      <div className="flex items-center justify-center gap-2">
                                        {canEdit && (
                                          <button
                                            type="button"
                                            title="Editar"
                                            onClick={() => openWithoutNfEditModal(row)}
                                            className="text-primary-500 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
                                          >
                                            <PencilIcon className="h-4 w-4" />
                                          </button>
                                        )}
                                        {canDelete && (
                                          <button
                                            type="button"
                                            title="Excluir"
                                            onClick={() =>
                                              openDeleteModal(
                                                'sem_nf',
                                                row.id,
                                                (row.description?.trim() && row.description.trim().slice(0, 80)) || `Registro ${row.id.slice(0, 8)}…`,
                                              )}
                                            className="text-red-400 hover:text-red-600 dark:hover:text-red-300 transition-colors"
                                          >
                                            <TrashIcon className="h-4 w-4" />
                                          </button>
                                        )}
                                      </div>
                                    </td>
                                  )}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          {renderPagination(semNfPage, semNfPagesCount, filteredSemNf.length, setSemNfPage)}
                        </>
                      )}
                    </div>
                  </>
                )}

                {activeTab === 'contracts' && (
                  <AdmDespesasContractsTab
                    contracts={contractsList}
                    allServices={allContractServices}
                    categorias={categorias}
                    centrosCusto={centrosCusto}
                    categoryMap={categoryMap}
                    costCenterMap={costCenterMap}
                    canEdit={canEdit}
                    canDelete={canDelete}
                    userId={user?.id}
                    onRefresh={refreshDespesasData}
                    pendingDetailDocumentId={pendingUnifiedDetail?.source === 'contracts' ? pendingUnifiedDetail.id : null}
                    onPendingDetailHandled={clearPendingUnifiedDetail}
                    onError={setPageError}
                    getErrorMessage={getErrorMessage}
                  />
                )}

                {activeTab === 'budgets' && (
                  <AdmDespesasBudgetsTab
                    budgets={budgetList}
                    allItems={allBudgetItems}
                    categorias={categorias}
                    centrosCusto={centrosCusto}
                    categoryMap={categoryMap}
                    costCenterMap={costCenterMap}
                    canEdit={canEdit}
                    canDelete={canDelete}
                    userId={user?.id}
                    onRefresh={refreshDespesasData}
                    pendingDetailDocumentId={pendingUnifiedDetail?.source === 'budgets' ? pendingUnifiedDetail.id : null}
                    onPendingDetailHandled={clearPendingUnifiedDetail}
                    onError={setPageError}
                    getErrorMessage={getErrorMessage}
                  />
                )}
              </div>
            )}
          </div>
        </PermissionGuard>
      </main>

      {/* Loading overlay detalhe */}
      {loadingDetail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <LoadingSpinner />
        </div>
      )}

      {/* Modal Detalhe NF-e */}
      {detailNfe && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-5xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-900 z-10">
              <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
                NF-e {detailNfe.header.number || '—'}
                {detailNfe.header.series && (
                  <span className="text-base font-normal text-gray-400 dark:text-gray-500 ml-2">
                    Série {detailNfe.header.series}
                  </span>
                )}
              </Heading>
              <Button variant="ghost" onClick={() => setDetailNfe(null)}>Fechar</Button>
            </div>

            <div className="p-6 space-y-6">
              {/* Cabeçalho */}
              <div>
                <Text size="sm" className="font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                  Dados da Nota
                </Text>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">Chave de Acesso</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5 break-all font-mono text-xs">{detailNfe.header.access_key || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">Data de Emissão</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5">{formatDate(detailNfe.header.issue_date)}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">Nº Itens</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5">{detailNfe.items.length}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">Emitente</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5">{detailNfe.header.issuer_name || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">CNPJ Emitente</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5">{formatDocument(detailNfe.header.issuer_cnpj)}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">Destinatário</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5">{detailNfe.header.recipient_name || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">CPF/CNPJ Destinatário</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5">{formatDocument(detailNfe.header.recipient_document)}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">Meio de Pagamento</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5">{detailNfe.header.payment_method || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">Data pagamento</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5">{formatDate(detailNfe.header.payment_date)}</dd>
                  </div>
                  <div className="sm:col-span-2">
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">Observações manuais</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5 whitespace-pre-wrap break-words">{detailNfe.header.manual_notes || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">Arquivo</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5">
                      {detailNfe.header.file_name || hasAdmNotaDownloadKey(detailNfe.header.file_name_storage, detailNfe.header.file_name) ? (
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="flex items-center gap-1 text-sm">
                            <DocumentIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                            {detailNfe.header.file_name ?? '—'}
                          </span>
                          {hasAdmNotaDownloadKey(detailNfe.header.file_name_storage, detailNfe.header.file_name) ? (
                            <button
                              type="button"
                              onClick={() =>
                                void openAdmNotaSignedDownload(
                                  detailNfe.header.file_name_storage,
                                  detailNfe.header.file_name
                                )
                              }
                              disabled={notaDocDownloadBusy}
                              className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 dark:text-primary-400 hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <ArrowDownTrayIcon className="h-3.5 w-3.5" />
                              Baixar documento
                            </button>
                          ) : null}
                        </div>
                      ) : (
                        '—'
                      )}
                    </dd>
                  </div>
                </dl>
              </div>

              {/* Valores */}
              <div>
                <Text size="sm" className="font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                  Valores
                </Text>
                <dl className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-3 text-sm">
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">Produtos</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5 font-medium">{formatCurrency(detailNfe.header.total_products)}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">Frete</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5">{formatCurrency(detailNfe.header.total_freight)}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">Desconto</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5">{formatCurrency(detailNfe.header.total_discount)}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">Total</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5 font-semibold text-base">{formatCurrency(detailNfe.header.total_value)}</dd>
                  </div>
                </dl>
              </div>

              {/* Itens */}
              <div>
                <Text size="sm" className="font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                  Itens ({detailNfe.items.length})
                </Text>
                {detailNfe.items.length === 0 ? (
                  <Text size="sm" color="muted">Nenhum item cadastrado.</Text>
                ) : (
                  <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
                      <thead className="bg-gray-50 dark:bg-gray-800">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Descrição</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">NCM</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Qtd</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Un</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Vl. Unit.</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Total</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Categoria</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Centro de Custo</th>
                          {(canDelete || canEdit) && <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Ações</th>}
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                        {detailNfe.items.map((item) => (
                          <tr key={item.id}>
                            <td className="px-3 py-2 text-gray-900 dark:text-gray-100">{item.description || '—'}</td>
                            <td className="px-3 py-2 text-gray-500 dark:text-gray-400">{item.ncm || '—'}</td>
                            <td className="px-3 py-2 text-right text-gray-900 dark:text-gray-100">{item.quantity ?? '—'}</td>
                            <td className="px-3 py-2 text-gray-500 dark:text-gray-400">{item.unit || '—'}</td>
                            <td className="px-3 py-2 text-right text-gray-900 dark:text-gray-100">{formatCurrency(item.unit_value)}</td>
                            <td className="px-3 py-2 text-right font-medium text-gray-900 dark:text-gray-100">{formatCurrency(item.total_value)}</td>
                            <td className="px-3 py-2 min-w-[160px]">
                              {item.category_id ? (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">
                                  {categoryMap.get(item.category_id) ?? item.category_id}
                                </span>
                              ) : (
                                <span className="text-gray-400 dark:text-gray-500 text-xs">—</span>
                              )}
                            </td>
                            <td className="px-3 py-2 min-w-[160px]">
                              {item.cost_center_id ? (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
                                  {costCenterMap.get(item.cost_center_id) ?? item.cost_center_id}
                                </span>
                              ) : (
                                <span className="text-gray-400 dark:text-gray-500 text-xs">—</span>
                              )}
                            </td>
                            {(canDelete || canEdit) && (
                              <td className="px-3 py-2 text-center">
                                <div className="flex items-center justify-center gap-2">
                                  {canEdit && (
                                    <button
                                      type="button"
                                      title="Editar item"
                                      onClick={() => openItemEditModal('nfe', item)}
                                      className="text-primary-500 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
                                    >
                                      <PencilIcon className="h-4 w-4" />
                                    </button>
                                  )}
                                  {canDelete && (
                                    <button
                                      type="button"
                                      title="Excluir item"
                                      onClick={() => void handleSoftDeleteItem('nfe', item)}
                                      disabled={savingItem === item.id}
                                      className="text-red-400 hover:text-red-600 dark:hover:text-red-300 transition-colors disabled:opacity-50"
                                    >
                                      <TrashIcon className="h-4 w-4" />
                                    </button>
                                  )}
                                </div>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Detalhe NFS-e */}
      {detailNfse && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-5xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-900 z-10">
              <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
                NFS-e {detailNfse.header.number || '—'}
              </Heading>
              <Button variant="ghost" onClick={() => setDetailNfse(null)}>Fechar</Button>
            </div>

            <div className="p-6 space-y-6">
              {/* Cabeçalho */}
              <div>
                <Text size="sm" className="font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                  Dados da Nota
                </Text>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">Chave de Acesso</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5 break-all font-mono text-xs">{detailNfse.header.access_key || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">Número RPS</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5">{detailNfse.header.rps_number || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">Data de Emissão</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5">{formatDate(detailNfse.header.issue_date)}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">Competência</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5">
                      {detailNfse.header.competence_date
                        ? new Date(detailNfse.header.competence_date + 'T00:00:00').toLocaleDateString('pt-BR', { month: '2-digit', year: 'numeric' })
                        : '—'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">Município</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5">{detailNfse.header.municipality || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">Prestador</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5">{detailNfse.header.provider_name || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">CNPJ Prestador</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5">{formatDocument(detailNfse.header.provider_cnpj)}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">Insc. Municipal</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5">{detailNfse.header.provider_im || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">Tomador</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5">{detailNfse.header.customer_name || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">CPF/CNPJ Tomador</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5">{formatDocument(detailNfse.header.customer_document)}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">Meio de Pagamento</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5">{detailNfse.header.payment_method || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">Data pagamento</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5">{formatDate(detailNfse.header.payment_date)}</dd>
                  </div>
                  <div className="sm:col-span-2">
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">Observações manuais</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5 whitespace-pre-wrap break-words">{detailNfse.header.manual_notes || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">Arquivo</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5">
                      {detailNfse.header.file_name || hasAdmNotaDownloadKey(detailNfse.header.file_name_storage, detailNfse.header.file_name) ? (
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="flex items-center gap-1 text-sm">
                            <DocumentIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                            {detailNfse.header.file_name ?? '—'}
                          </span>
                          {hasAdmNotaDownloadKey(detailNfse.header.file_name_storage, detailNfse.header.file_name) ? (
                            <button
                              type="button"
                              onClick={() =>
                                void openAdmNotaSignedDownload(
                                  detailNfse.header.file_name_storage,
                                  detailNfse.header.file_name
                                )
                              }
                              disabled={notaDocDownloadBusy}
                              className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 dark:text-primary-400 hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <ArrowDownTrayIcon className="h-3.5 w-3.5" />
                              Baixar documento
                            </button>
                          ) : null}
                        </div>
                      ) : (
                        '—'
                      )}
                    </dd>
                  </div>
                </dl>
              </div>

              {/* Valores */}
              <div>
                <Text size="sm" className="font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                  Valores
                </Text>
                <dl className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-3 text-sm">
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">Total Serviços</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5 font-medium">{formatCurrency(detailNfse.header.total_services)}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">Desconto</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5">{formatCurrency(detailNfse.header.total_discount)}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">Valor Líquido</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5 font-semibold text-base">{formatCurrency(detailNfse.header.net_value)}</dd>
                  </div>
                </dl>
              </div>

              {/* Serviços */}
              <div>
                <Text size="sm" className="font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                  Serviços ({detailNfse.items.length})
                </Text>
                {detailNfse.items.length === 0 ? (
                  <Text size="sm" color="muted">Nenhum serviço cadastrado.</Text>
                ) : (
                  <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
                      <thead className="bg-gray-50 dark:bg-gray-800">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Descrição</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Código</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Qtd</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Vl. Unit.</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Total</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Categoria</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Centro de Custo</th>
                          {(canDelete || canEdit) && <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Ações</th>}
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                        {detailNfse.items.map((item) => (
                          <tr key={item.id}>
                            <td className="px-3 py-2 text-gray-900 dark:text-gray-100">{item.description || '—'}</td>
                            <td className="px-3 py-2 text-gray-500 dark:text-gray-400">{item.service_code || '—'}</td>
                            <td className="px-3 py-2 text-right text-gray-900 dark:text-gray-100">{item.quantity ?? '—'}</td>
                            <td className="px-3 py-2 text-right text-gray-900 dark:text-gray-100">{formatCurrency(item.unit_value)}</td>
                            <td className="px-3 py-2 text-right font-medium text-gray-900 dark:text-gray-100">{formatCurrency(item.total_value)}</td>
                            <td className="px-3 py-2 min-w-[160px]">
                              {item.category_id ? (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">
                                  {categoryMap.get(item.category_id) ?? item.category_id}
                                </span>
                              ) : (
                                <span className="text-gray-400 dark:text-gray-500 text-xs">—</span>
                              )}
                            </td>
                            <td className="px-3 py-2 min-w-[160px]">
                              {item.cost_center_id ? (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
                                  {costCenterMap.get(item.cost_center_id) ?? item.cost_center_id}
                                </span>
                              ) : (
                                <span className="text-gray-400 dark:text-gray-500 text-xs">—</span>
                              )}
                            </td>
                            {(canDelete || canEdit) && (
                              <td className="px-3 py-2 text-center">
                                <div className="flex items-center justify-center gap-2">
                                  {canEdit && (
                                    <button
                                      type="button"
                                      title="Editar item"
                                      onClick={() => openItemEditModal('nfse', item)}
                                      className="text-primary-500 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
                                    >
                                      <PencilIcon className="h-4 w-4" />
                                    </button>
                                  )}
                                  {canDelete && (
                                    <button
                                      type="button"
                                      title="Excluir item"
                                      onClick={() => void handleSoftDeleteItem('nfse', item)}
                                      disabled={savingItem === item.id}
                                      className="text-red-400 hover:text-red-600 dark:hover:text-red-300 transition-colors disabled:opacity-50"
                                    >
                                      <TrashIcon className="h-4 w-4" />
                                    </button>
                                  )}
                                </div>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {itemEditModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-2xl mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-4">
              Editar item {itemEditModal.tableType.toUpperCase()}
            </Heading>
            <div className="space-y-4">
              <div>
                <Text size="sm" color="muted">Nota: {itemEditModal.headerNumber}</Text>
                <Text size="sm" color="muted" className="truncate">Item: {itemEditModal.description || itemEditModal.itemId}</Text>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Descrição</label>
                  <Input
                    type="text"
                    value={itemEditModal.description}
                    onChange={(e) => setItemEditModal((prev) => prev ? { ...prev, description: e.target.value } : prev)}
                  />
                </div>
                {itemEditModal.tableType === 'nfe' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">NCM</label>
                      <Input
                        type="text"
                        value={itemEditModal.ncm}
                        onChange={(e) => setItemEditModal((prev) => prev ? { ...prev, ncm: e.target.value } : prev)}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Unidade</label>
                      <Input
                        type="text"
                        value={itemEditModal.unit}
                        onChange={(e) => setItemEditModal((prev) => prev ? { ...prev, unit: e.target.value } : prev)}
                      />
                    </div>
                  </>
                )}
                {itemEditModal.tableType === 'nfse' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Código do serviço</label>
                    <Input
                      type="text"
                      value={itemEditModal.serviceCode}
                      onChange={(e) => setItemEditModal((prev) => prev ? { ...prev, serviceCode: e.target.value } : prev)}
                    />
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Quantidade</label>
                  <Input
                    type="number"
                    step="any"
                    value={itemEditModal.quantity}
                    onChange={(e) => setItemEditModal((prev) => prev ? { ...prev, quantity: e.target.value } : prev)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Valor unitário</label>
                  <Input
                    type="number"
                    step="any"
                    value={itemEditModal.unitValue}
                    onChange={(e) => setItemEditModal((prev) => prev ? { ...prev, unitValue: e.target.value } : prev)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Valor total</label>
                  <Input
                    type="number"
                    step="any"
                    value={itemEditModal.totalValue}
                    onChange={(e) => setItemEditModal((prev) => prev ? { ...prev, totalValue: e.target.value } : prev)}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Categoria</label>
                <select
                  value={itemEditModal.categoryId}
                  onChange={(e) => setItemEditModal((prev) => prev ? { ...prev, categoryId: e.target.value } : prev)}
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-white/5 px-3 py-2 text-sm text-gray-900 dark:text-white shadow-sm"
                >
                  <option value="">- Nenhuma -</option>
                  {categorias.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Centro de custo</label>
                <select
                  value={itemEditModal.costCenterId}
                  onChange={(e) => setItemEditModal((prev) => prev ? { ...prev, costCenterId: e.target.value } : prev)}
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-white/5 px-3 py-2 text-sm text-gray-900 dark:text-white shadow-sm"
                >
                  <option value="">- Nenhum -</option>
                  {centrosCusto.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <Button variant="outline" onClick={closeItemEditModal} disabled={submittingItemEdit}>
                Cancelar
              </Button>
              <Button variant="solid" onClick={() => void handleSaveItemEdit()} disabled={submittingItemEdit}>
                {submittingItemEdit ? 'Salvando...' : 'Salvar'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {withoutNfDetailModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="without-nf-detail-title"
            className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-3xl mx-4 max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-900 z-10">
              <Heading as="h2" size="2xl" id="without-nf-detail-title" className="text-gray-900 dark:text-white">
                Planilha (sem NF) - detalhe
              </Heading>
              <Button variant="ghost" onClick={() => setWithoutNfDetailModal(null)}>
                Fechar
              </Button>
            </div>
            <div className="p-6">
              <Text size="sm" className="font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                Registro importado
              </Text>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                <div className="sm:col-span-2">
                  <dt className="text-gray-500 dark:text-gray-400 font-medium">ID</dt>
                  <dd className="text-gray-900 dark:text-white mt-0.5 break-all font-mono text-xs">{withoutNfDetailModal.id}</dd>
                </div>
                <div className="sm:col-span-2">
                  <dt className="text-gray-500 dark:text-gray-400 font-medium">Lote de importação</dt>
                  <dd className="text-gray-900 dark:text-white mt-0.5 break-all font-mono text-xs">{withoutNfDetailModal.import_batch_id}</dd>
                </div>
                <div>
                  <dt className="text-gray-500 dark:text-gray-400 font-medium">Arquivo de origem</dt>
                  <dd className="text-gray-900 dark:text-white mt-0.5 break-words">{withoutNfDetailModal.source_file_name || '—'}</dd>
                </div>
                <div>
                  <dt className="text-gray-500 dark:text-gray-400 font-medium">Tipo de pagamento</dt>
                  <dd className="text-gray-900 dark:text-white mt-0.5">{withoutNfDetailModal.payment_type || '—'}</dd>
                </div>
                <div>
                  <dt className="text-gray-500 dark:text-gray-400 font-medium">Centro de custo</dt>
                  <dd className="text-gray-900 dark:text-white mt-0.5">
                    {withoutNfDetailModal.cost_center_id
                      ? (costCenterMap.get(withoutNfDetailModal.cost_center_id) ?? withoutNfDetailModal.cost_center_id)
                      : '—'}
                  </dd>
                </div>
                <div>
                  <dt className="text-gray-500 dark:text-gray-400 font-medium">Categoria</dt>
                  <dd className="text-gray-900 dark:text-white mt-0.5">
                    {withoutNfDetailModal.category_id
                      ? (categoryMap.get(withoutNfDetailModal.category_id) ?? withoutNfDetailModal.category_id)
                      : '—'}
                  </dd>
                </div>
                <div>
                  <dt className="text-gray-500 dark:text-gray-400 font-medium">Data despesa (planilha)</dt>
                  <dd className="text-gray-900 dark:text-white mt-0.5">{formatDate(withoutNfDetailModal.expense_date)}</dd>
                </div>
                <div>
                  <dt className="text-gray-500 dark:text-gray-400 font-medium">Data pagamento</dt>
                  <dd className="text-gray-900 dark:text-white mt-0.5">{formatDate(withoutNfDetailModal.payment_date)}</dd>
                </div>
                <div>
                  <dt className="text-gray-500 dark:text-gray-400 font-medium">Parcela</dt>
                  <dd className="text-gray-900 dark:text-white mt-0.5">{withoutNfDetailModal.installment || '—'}</dd>
                </div>
                <div className="sm:col-span-2">
                  <dt className="text-gray-500 dark:text-gray-400 font-medium">Descrição</dt>
                  <dd className="text-gray-900 dark:text-white mt-0.5 whitespace-pre-wrap break-words">{withoutNfDetailModal.description || '—'}</dd>
                </div>
                <div>
                  <dt className="text-gray-500 dark:text-gray-400 font-medium">Valor pago</dt>
                  <dd className="text-gray-900 dark:text-white mt-0.5 font-medium">{formatCurrency(withoutNfDetailModal.amount_paid)}</dd>
                </div>
                <div>
                  <dt className="text-gray-500 dark:text-gray-400 font-medium">Valor a pagar</dt>
                  <dd className="text-gray-900 dark:text-white mt-0.5 font-medium">{formatCurrency(withoutNfDetailModal.amount_to_pay)}</dd>
                </div>
                <div className="sm:col-span-2">
                  <dt className="text-gray-500 dark:text-gray-400 font-medium">Observações (planilha)</dt>
                  <dd className="text-gray-900 dark:text-white mt-0.5 whitespace-pre-wrap break-words">{withoutNfDetailModal.notes || '—'}</dd>
                </div>
                <div className="sm:col-span-2">
                  <dt className="text-gray-500 dark:text-gray-400 font-medium">Observações manuais</dt>
                  <dd className="text-gray-900 dark:text-white mt-0.5 whitespace-pre-wrap break-words">{withoutNfDetailModal.manual_notes || '—'}</dd>
                </div>
                <div>
                  <dt className="text-gray-500 dark:text-gray-400 font-medium">Importado por (ID)</dt>
                  <dd className="text-gray-900 dark:text-white mt-0.5 break-all font-mono text-xs">{withoutNfDetailModal.uploaded_by || '—'}</dd>
                </div>
                <div>
                  <dt className="text-gray-500 dark:text-gray-400 font-medium">Criado em</dt>
                  <dd className="text-gray-900 dark:text-white mt-0.5">{new Date(withoutNfDetailModal.created_at).toLocaleString('pt-BR')}</dd>
                </div>
                <div>
                  <dt className="text-gray-500 dark:text-gray-400 font-medium">Atualizado em</dt>
                  <dd className="text-gray-900 dark:text-white mt-0.5">{new Date(withoutNfDetailModal.updated_at).toLocaleString('pt-BR')}</dd>
                </div>
                {withoutNfDetailModal.deleted_at && (
                  <>
                    <div>
                      <dt className="text-gray-500 dark:text-gray-400 font-medium">Excluído em</dt>
                      <dd className="text-gray-900 dark:text-white mt-0.5">{new Date(withoutNfDetailModal.deleted_at).toLocaleString('pt-BR')}</dd>
                    </div>
                    <div>
                      <dt className="text-gray-500 dark:text-gray-400 font-medium">Excluído por (ID)</dt>
                      <dd className="text-gray-900 dark:text-white mt-0.5 break-all font-mono text-xs">{withoutNfDetailModal.deleted_by || '—'}</dd>
                    </div>
                    <div className="sm:col-span-2">
                      <dt className="text-gray-500 dark:text-gray-400 font-medium">Motivo da exclusão</dt>
                      <dd className="text-gray-900 dark:text-white mt-0.5 whitespace-pre-wrap break-words">{withoutNfDetailModal.deleted_reason || '—'}</dd>
                    </div>
                  </>
                )}
              </dl>
              {canEdit && (
                <div className="mt-6 flex justify-end gap-2">
                  <Button
                    variant="solid"
                    onClick={() => {
                      const row = withoutNfDetailModal
                      setWithoutNfDetailModal(null)
                      openWithoutNfEditModal(row)
                    }}
                  >
                    Editar registro
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {withoutNfEditModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-2xl mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-4">
              Editar planilha (sem NF)
            </Heading>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Tipo de pagamento</label>
                  <Input
                    type="text"
                    value={withoutNfEditModal.paymentType}
                    onChange={(e) => setWithoutNfEditModal((prev) => (prev ? { ...prev, paymentType: e.target.value } : prev))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Data despesa (planilha)</label>
                  <Input
                    type="date"
                    value={withoutNfEditModal.expenseDate}
                    onChange={(e) => setWithoutNfEditModal((prev) => (prev ? { ...prev, expenseDate: e.target.value } : prev))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Data pagamento</label>
                  <Input
                    type="date"
                    value={withoutNfEditModal.paymentDate}
                    onChange={(e) => setWithoutNfEditModal((prev) => (prev ? { ...prev, paymentDate: e.target.value } : prev))}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Descrição</label>
                  <Input
                    type="text"
                    value={withoutNfEditModal.description}
                    onChange={(e) => setWithoutNfEditModal((prev) => (prev ? { ...prev, description: e.target.value } : prev))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Parcela</label>
                  <Input
                    type="text"
                    value={withoutNfEditModal.installment}
                    onChange={(e) => setWithoutNfEditModal((prev) => (prev ? { ...prev, installment: e.target.value } : prev))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Valor pago</label>
                  <Input
                    type="number"
                    step="any"
                    value={withoutNfEditModal.amountPaid}
                    onChange={(e) => setWithoutNfEditModal((prev) => (prev ? { ...prev, amountPaid: e.target.value } : prev))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Valor a pagar</label>
                  <Input
                    type="number"
                    step="any"
                    value={withoutNfEditModal.amountToPay}
                    onChange={(e) => setWithoutNfEditModal((prev) => (prev ? { ...prev, amountToPay: e.target.value } : prev))}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Observações (planilha)</label>
                  <Input
                    type="text"
                    value={withoutNfEditModal.notes}
                    onChange={(e) => setWithoutNfEditModal((prev) => (prev ? { ...prev, notes: e.target.value } : prev))}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Observações manuais</label>
                  <textarea
                    rows={3}
                    value={withoutNfEditModal.manualNotes}
                    onChange={(e) => setWithoutNfEditModal((prev) => (prev ? { ...prev, manualNotes: e.target.value } : prev))}
                    className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Categoria</label>
                <select
                  value={withoutNfEditModal.categoryId}
                  onChange={(e) => setWithoutNfEditModal((prev) => (prev ? { ...prev, categoryId: e.target.value } : prev))}
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-white/5 px-3 py-2 text-sm text-gray-900 dark:text-white shadow-sm"
                >
                  <option value="">- Nenhuma -</option>
                  {categorias.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Centro de custo</label>
                <select
                  value={withoutNfEditModal.costCenterId}
                  onChange={(e) => setWithoutNfEditModal((prev) => (prev ? { ...prev, costCenterId: e.target.value } : prev))}
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-white/5 px-3 py-2 text-sm text-gray-900 dark:text-white shadow-sm"
                >
                  <option value="">- Nenhum -</option>
                  {centrosCusto.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <Button variant="outline" onClick={closeWithoutNfEditModal} disabled={submittingWithoutNfEdit}>
                Cancelar
              </Button>
              <Button variant="solid" onClick={() => void handleSaveWithoutNfEdit()} disabled={submittingWithoutNfEdit}>
                {submittingWithoutNfEdit ? 'Salvando...' : 'Salvar'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de upload / cadastrar despesa */}
      {uploadModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg mx-4 p-6 max-h-[90vh] overflow-y-auto">

            {/* Cabeçalho */}
            <div className="flex items-center justify-between mb-4">
              <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                Cadastrar Despesas
              </Heading>
              <button
                type="button"
                onClick={closeUploadModal}
                disabled={isProcessing || spreadsheetSubmitting}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 disabled:opacity-40 transition-colors"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="mb-4 space-y-2">
              <label className="flex items-start gap-2 cursor-pointer text-sm text-gray-800 dark:text-gray-100">
                <input
                  type="radio"
                  name="cadastroSource"
                  className="mt-1"
                  checked={cadastroSource === 'notas_fiscais'}
                  onChange={() => handleCadastroSourceChange('notas_fiscais')}
                  disabled={isProcessing || spreadsheetSubmitting}
                />
                <span>
                  <span className="font-medium">NF-e e NFS-e</span>
                  <span className="block text-gray-500 dark:text-gray-400 text-xs">Enviar PDFs para processamento.</span>
                </span>
              </label>
              <label className="flex items-start gap-2 cursor-pointer text-sm text-gray-800 dark:text-gray-100">
                <input
                  type="radio"
                  name="cadastroSource"
                  className="mt-1"
                  checked={cadastroSource === 'planilha_pagamentos'}
                  onChange={() => handleCadastroSourceChange('planilha_pagamentos')}
                  disabled={isProcessing || spreadsheetSubmitting}
                />
                <span>
                  <span className="font-medium">Planilha de Pagamentos</span>
                  <span className="block text-gray-500 dark:text-gray-400 text-xs">Importar linhas de um Excel (.xlsx ou .xls).</span>
                </span>
              </label>
              <label className="flex items-start gap-2 cursor-pointer text-sm text-gray-800 dark:text-gray-100">
                <input
                  type="radio"
                  name="cadastroSource"
                  className="mt-1"
                  checked={cadastroSource === 'contratos'}
                  onChange={() => handleCadastroSourceChange('contratos')}
                  disabled={isProcessing || spreadsheetSubmitting}
                />
                <span>
                  <span className="font-medium">Contratos</span>
                  <span className="block text-gray-500 dark:text-gray-400 text-xs">
                    Enviar PDFs ou imagens de contratos para processamento.
                  </span>
                </span>
              </label>
              <label className="flex items-start gap-2 cursor-pointer text-sm text-gray-800 dark:text-gray-100">
                <input
                  type="radio"
                  name="cadastroSource"
                  className="mt-1"
                  checked={cadastroSource === 'orcamentos_pedidos'}
                  onChange={() => handleCadastroSourceChange('orcamentos_pedidos')}
                  disabled={isProcessing || spreadsheetSubmitting}
                />
                <span>
                  <span className="font-medium">Orçamentos / Pedidos</span>
                  <span className="block text-gray-500 dark:text-gray-400 text-xs">
                    Enviar PDFs ou imagens de orçamentos ou pedidos para processamento.
                  </span>
                </span>
              </label>
            </div>

            {(cadastroSource === 'notas_fiscais' || isCadastroUploadContratosTipo(cadastroSource)) && (
              <>
            {/* Zona de seleção: NF-e só PDF; contratos/orçamentos PDF + imagem, vários arquivos */}
            {!isProcessing && (
              <div
                className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-5 text-center cursor-pointer hover:border-primary-400 dark:hover:border-primary-500 transition-colors mb-4"
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  key={`cadastro-files-${cadastroSource}`}
                  ref={fileInputRef}
                  type="file"
                  accept={
                    cadastroSource === 'notas_fiscais'
                      ? 'application/pdf,.pdf'
                      : 'application/pdf,.pdf,image/*'
                  }
                  multiple
                  className="hidden"
                  onChange={handleFileChange}
                />
                <ArrowUpTrayIcon className="mx-auto h-7 w-7 text-gray-400 dark:text-gray-500 mb-1" />
                <Text size="sm" color="muted">
                  {isCadastroUploadContratosTipo(cadastroSource) ? (
                    <>
                      Clique para selecionar um ou mais arquivos (PDF ou imagens)
                      {uploadFiles.length > 0 && ' - adicionar mais à fila'}
                    </>
                  ) : (
                    <>
                      Clique para adicionar PDFs
                      {uploadFiles.length > 0 && ' (adicionar mais)'}
                    </>
                  )}
                </Text>
              </div>
            )}

            {/* Resumo do progresso quando processando */}
            {(isProcessing || processingDone) && (() => {
              const total = uploadFiles.length
              const done = uploadFiles.filter((f) => f.status === 'success' || f.status === 'error').length
              const success = uploadFiles.filter((f) => f.status === 'success').length
              const errors = uploadFiles.filter((f) => f.status === 'error').length
              const pct = total > 0 ? Math.round((done / total) * 100) : 0
              return (
                <div className="mb-4 space-y-2">
                  <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                    <span>{done} de {total} arquivo{total !== 1 ? 's' : ''} processado{total !== 1 ? 's' : ''}</span>
                    <span className="font-medium">{pct}%</span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all duration-300 ${errors > 0 && done === total ? 'bg-yellow-500' : 'bg-primary-600'}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
                    {processingStartedAt && (
                      <span>Início: <span className="font-mono text-gray-700 dark:text-gray-300">{formatTime(processingStartedAt)}</span></span>
                    )}
                    {processingDone && processingFinishedAt && (
                      <span>Fim: <span className="font-mono text-gray-700 dark:text-gray-300">{formatTime(processingFinishedAt)}</span></span>
                    )}
                    {processingDone && success > 0 && (
                      <span className="text-green-600 dark:text-green-400 font-medium">{success} enviado{success !== 1 ? 's' : ''}</span>
                    )}
                    {processingDone && errors > 0 && (
                      <span className="text-red-600 dark:text-red-400 font-medium">{errors} com erro</span>
                    )}
                  </div>
                </div>
              )
            })()}

            {/* Lista de arquivos */}
            {uploadFiles.length > 0 && (
              <ul className="space-y-2 max-h-64 overflow-y-auto mb-4 pr-1">
                {uploadFiles.map((entry, idx) => (
                  <li
                    key={entry.file.name + idx}
                    className="flex items-center gap-3 rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2 bg-gray-50 dark:bg-gray-700/50"
                  >
                    {/* Ícone de status */}
                    <div className="flex-shrink-0">
                      {entry.status === 'pending' && (
                        <DocumentIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                      )}
                      {entry.status === 'sending' && (
                        <svg className="animate-spin h-5 w-5 text-primary-500" viewBox="0 0 24 24" fill="none">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                        </svg>
                      )}
                      {entry.status === 'success' && (
                        <CheckCircleIcon className="h-5 w-5 text-green-500" />
                      )}
                      {entry.status === 'error' && (
                        <div className="h-5 w-5 rounded-full bg-red-100 dark:bg-red-900/40 flex items-center justify-center">
                          <XMarkIcon className="h-3 w-3 text-red-500" />
                        </div>
                      )}
                    </div>

                    {/* Nome e info */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate" title={entry.file.name}>
                        {entry.file.name}
                      </p>
                      <p className="text-xs text-gray-400 dark:text-gray-500">
                        {entry.status === 'pending' && `${(entry.file.size / 1024).toFixed(1)} KB`}
                        {entry.status === 'sending' && 'Enviando...'}
                        {entry.status === 'success' && (
                          <span className="text-green-600 dark:text-green-400">
                            Concluído{entry.finishedAt ? ` às ${formatTime(entry.finishedAt)}` : ''}
                          </span>
                        )}
                        {entry.status === 'error' && (
                          <span className="text-red-500 dark:text-red-400">
                            {entry.error ?? 'Erro ao enviar'}
                            {entry.finishedAt ? ` — ${formatTime(entry.finishedAt)}` : ''}
                          </span>
                        )}
                      </p>
                    </div>

                    {/* Remover (somente antes de processar) */}
                    {!isProcessing && entry.status === 'pending' && (
                      <button
                        type="button"
                        onClick={() => removeFile(idx)}
                        className="flex-shrink-0 text-gray-300 hover:text-red-400 dark:text-gray-600 dark:hover:text-red-400 transition-colors"
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}

            {uploadFiles.length === 0 && !isProcessing && (
              <Text size="sm" color="muted" className="text-center mb-4">
                Nenhum arquivo selecionado.
              </Text>
            )}
              </>
            )}

            {cadastroSource === 'planilha_pagamentos' && (
              <>
                {!spreadsheetImportDone && !spreadsheetSubmitting && (
                  <div
                    className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-5 text-center cursor-pointer hover:border-primary-400 dark:hover:border-primary-500 transition-colors mb-4"
                    onClick={() => excelInputRef.current?.click()}
                  >
                    <input
                      ref={excelInputRef}
                      type="file"
                      accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                      className="hidden"
                      onChange={handleExcelFileChange}
                    />
                    <ArrowUpTrayIcon className="mx-auto h-7 w-7 text-gray-400 dark:text-gray-500 mb-1" />
                    <Text size="sm" color="muted">
                      {excelFileName ? `Arquivo: ${excelFileName} (clique para trocar)` : 'Clique para selecionar a planilha Excel'}
                    </Text>
                  </div>
                )}
                {excelSheetNames.length > 0 && !spreadsheetImportDone && !spreadsheetSubmitting && (
                  <div className="mb-4">
                    <Text id="excel-sheet-choice-label" size="sm" className="font-medium text-gray-900 dark:text-white mb-2">
                      Aba do Excel
                    </Text>
                    <div className="space-y-2" role="radiogroup" aria-labelledby="excel-sheet-choice-label">
                      {excelSheetNames.map((name) => (
                        <label
                          key={name}
                          className="flex items-start gap-2 cursor-pointer text-sm text-gray-800 dark:text-gray-100"
                        >
                          <input
                            type="radio"
                            name="excelSheet"
                            value={name}
                            className="mt-1"
                            checked={excelSelectedSheet === name}
                            onChange={() => setExcelSelectedSheet(name)}
                          />
                          <span>{name}</span>
                        </label>
                      ))}
                    </div>
                    <Text size="sm" color="muted" className="mt-2">
                      Selecione a aba em que estão os dados a importar.
                    </Text>
                  </div>
                )}
                {!spreadsheetImportDone && !spreadsheetSubmitting && (
                  <div className="mb-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="excel-header-row" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                        Linha do cabeçalho (Excel)
                      </label>
                      <Input
                        id="excel-header-row"
                        type="number"
                        min={1}
                        step={1}
                        value={excelHeaderRow}
                        onChange={(e) => setExcelHeaderRow(e.target.value)}
                      />
                      <Text size="sm" color="muted" className="mt-1.5">
                        Linha em que estão os títulos das colunas (TIPO PGTO, DATA, etc.). Padrão: 1.
                      </Text>
                    </div>
                    <div>
                      <label htmlFor="excel-first-data-row" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                        Linha do primeiro registro (Excel)
                      </label>
                      <Input
                        id="excel-first-data-row"
                        type="number"
                        min={1}
                        step={1}
                        value={excelFirstDataRow}
                        onChange={(e) => setExcelFirstDataRow(e.target.value)}
                      />
                      <Text size="sm" color="muted" className="mt-1.5">
                        Primeira linha de dados; deve ser maior que a linha do cabeçalho. Padrão: 2.
                      </Text>
                    </div>
                  </div>
                )}
                {excelParseError && (
                  <div className="mb-4 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-3 py-2">
                    {excelParseError}
                  </div>
                )}
                {excelParsedRows && excelParsedRows.length > 0 && !spreadsheetImportDone && (
                  <Text size="sm" className="mb-4 text-gray-700 dark:text-gray-300">
                    {excelParsedRows.length} linha{excelParsedRows.length !== 1 ? 's' : ''} pronta{excelParsedRows.length !== 1 ? 's' : ''} para enviar.
                  </Text>
                )}
                {spreadsheetSubmitting && (
                  <div className="mb-4 flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                    <svg className="animate-spin h-5 w-5 text-primary-500" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                    </svg>
                    Gravando no sistema...
                  </div>
                )}
                {spreadsheetImportDone && (
                  <Text size="sm" className="mb-4 text-green-600 dark:text-green-400 font-medium">
                    Importação concluída com sucesso.
                  </Text>
                )}
                {!excelFileName && !excelParseError && !spreadsheetImportDone && !spreadsheetSubmitting && (
                  <Text size="sm" color="muted" className="text-center mb-4">
                    Nenhuma planilha selecionada.
                  </Text>
                )}
              </>
            )}

            {/* Rodapé */}
            <div className="flex justify-end gap-3 pt-2 border-t border-gray-200 dark:border-gray-700">
              <Button
                variant="outline"
                size="md"
                onClick={closeUploadModal}
                disabled={isProcessing || spreadsheetSubmitting}
              >
                {(cadastroSource === 'notas_fiscais' && processingDone) ||
                (isCadastroUploadContratosTipo(cadastroSource) && processingDone) ||
                (cadastroSource === 'planilha_pagamentos' && spreadsheetImportDone)
                  ? 'Fechar'
                  : 'Cancelar'}
              </Button>
              {(cadastroSource === 'notas_fiscais' || isCadastroUploadContratosTipo(cadastroSource)) &&
                !processingDone && (
                <Button
                  variant="solid"
                  size="md"
                  onClick={handleUploadSubmit}
                  disabled={uploadFiles.filter((f) => f.status === 'pending').length === 0 || isProcessing}
                >
                  {isProcessing ? (
                    <>
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                      </svg>
                      Processando...
                    </>
                  ) : (
                    <>
                      <ArrowUpTrayIcon className="h-4 w-4" />
                      Enviar {uploadFiles.filter((f) => f.status === 'pending').length > 0
                        ? `${uploadFiles.filter((f) => f.status === 'pending').length} arquivo${uploadFiles.filter((f) => f.status === 'pending').length !== 1 ? 's' : ''}`
                        : ''}
                    </>
                  )}
                </Button>
              )}
              {cadastroSource === 'planilha_pagamentos' && !spreadsheetImportDone && (
                <Button
                  variant="solid"
                  size="md"
                  onClick={() => void handleSpreadsheetSubmit()}
                  disabled={!excelParsedRows?.length || spreadsheetSubmitting}
                >
                  {spreadsheetSubmitting ? (
                    <>
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                      </svg>
                      Enviando...
                    </>
                  ) : (
                    <>
                      <ArrowUpTrayIcon className="h-4 w-4" />
                      Enviar
                      {excelParsedRows && excelParsedRows.length > 0
                        ? ` (${excelParsedRows.length})`
                        : ''}
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {headerEditModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-5xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-800 z-10">
              <div>
                <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                  Editar Cabeçalho {headerEditModal.type === 'nfe' ? 'NF-e' : 'NFS-e'}
                </Heading>
                <Text size="sm" color="muted" className="mt-1">
                  Atualize os dados principais do cabeçalho da nota.
                </Text>
              </div>
              <Button variant="ghost" onClick={closeHeaderEditModal} disabled={submittingHeaderEdit}>
                <XMarkIcon className="h-5 w-5" />
              </Button>
            </div>

            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Número da nota</label>
                  <Input type="text" value={headerEditModal.number} onChange={(e) => setHeaderEditModal((prev) => prev ? { ...prev, number: e.target.value } : prev)} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Data de emissão</label>
                  <Input type="date" value={headerEditModal.issueDate} onChange={(e) => setHeaderEditModal((prev) => prev ? { ...prev, issueDate: e.target.value } : prev)} />
                </div>
                {headerEditModal.type === 'nfe' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Série</label>
                    <Input type="text" value={headerEditModal.series} onChange={(e) => setHeaderEditModal((prev) => prev ? { ...prev, series: e.target.value } : prev)} />
                  </div>
                )}
                <div className={headerEditModal.type === 'nfe' ? 'md:col-span-2' : 'md:col-span-2'}>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{headerEditModal.type === 'nfe' ? 'Emitente' : 'Prestador'}</label>
                  <Input type="text" value={headerEditModal.name} onChange={(e) => setHeaderEditModal((prev) => prev ? { ...prev, name: e.target.value } : prev)} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{headerEditModal.type === 'nfe' ? 'CNPJ emitente' : 'CNPJ prestador'}</label>
                  <Input type="text" value={headerEditModal.document} onChange={(e) => setHeaderEditModal((prev) => prev ? { ...prev, document: e.target.value } : prev)} />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Chave de acesso</label>
                  <Input type="text" value={headerEditModal.accessKey} onChange={(e) => setHeaderEditModal((prev) => prev ? { ...prev, accessKey: e.target.value } : prev)} />
                </div>

                {headerEditModal.type === 'nfe' && (
                  <>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Destinatário</label>
                      <Input type="text" value={headerEditModal.recipientName} onChange={(e) => setHeaderEditModal((prev) => prev ? { ...prev, recipientName: e.target.value } : prev)} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">CPF/CNPJ destinatário</label>
                      <Input type="text" value={headerEditModal.recipientDocument} onChange={(e) => setHeaderEditModal((prev) => prev ? { ...prev, recipientDocument: e.target.value } : prev)} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Total produtos</label>
                      <Input type="number" step="0.01" value={headerEditModal.totalProducts} onChange={(e) => setHeaderEditModal((prev) => prev ? { ...prev, totalProducts: e.target.value } : prev)} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Frete</label>
                      <Input type="number" step="0.01" value={headerEditModal.totalFreight} onChange={(e) => setHeaderEditModal((prev) => prev ? { ...prev, totalFreight: e.target.value } : prev)} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Desconto</label>
                      <Input type="number" step="0.01" value={headerEditModal.totalDiscount} onChange={(e) => setHeaderEditModal((prev) => prev ? { ...prev, totalDiscount: e.target.value } : prev)} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Valor total</label>
                      <Input type="number" step="0.01" value={headerEditModal.totalValue} onChange={(e) => setHeaderEditModal((prev) => prev ? { ...prev, totalValue: e.target.value } : prev)} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Meio de pagamento</label>
                      <Input type="text" value={headerEditModal.paymentMethod} onChange={(e) => setHeaderEditModal((prev) => prev ? { ...prev, paymentMethod: e.target.value } : prev)} />
                    </div>
                    {hasAdmNotaDownloadKey(headerEditModal.fileNameStorage, headerEditModal.fileName) && (
                      <div className="md:col-span-2">
                        <button
                          type="button"
                          onClick={() => void openAdmNotaSignedDownloadFromHeaderEdit()}
                          disabled={notaDocDownloadBusy}
                          className="inline-flex shrink-0 items-center justify-center gap-1 px-3 py-2 text-sm font-medium text-primary-600 dark:text-primary-400 border border-primary-200 dark:border-primary-800 rounded-lg hover:bg-primary-50 dark:hover:bg-primary-900/20 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <ArrowDownTrayIcon className="h-4 w-4" />
                          Baixar documento
                        </button>
                        {headerEditFileDownloadError && (
                          <p className="text-sm text-red-600 dark:text-red-400 mt-2">{headerEditFileDownloadError}</p>
                        )}
                      </div>
                    )}
                  </>
                )}
                {headerEditModal.type === 'nfse' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">RPS</label>
                      <Input type="text" value={headerEditModal.rpsNumber} onChange={(e) => setHeaderEditModal((prev) => prev ? { ...prev, rpsNumber: e.target.value } : prev)} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Competência</label>
                      <Input type="date" value={headerEditModal.competenceDate} onChange={(e) => setHeaderEditModal((prev) => prev ? { ...prev, competenceDate: e.target.value } : prev)} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Inscrição municipal</label>
                      <Input type="text" value={headerEditModal.providerIm} onChange={(e) => setHeaderEditModal((prev) => prev ? { ...prev, providerIm: e.target.value } : prev)} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Município</label>
                      <Input type="text" value={headerEditModal.municipality} onChange={(e) => setHeaderEditModal((prev) => prev ? { ...prev, municipality: e.target.value } : prev)} />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Tomador</label>
                      <Input type="text" value={headerEditModal.recipientName} onChange={(e) => setHeaderEditModal((prev) => prev ? { ...prev, recipientName: e.target.value } : prev)} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">CPF/CNPJ tomador</label>
                      <Input type="text" value={headerEditModal.recipientDocument} onChange={(e) => setHeaderEditModal((prev) => prev ? { ...prev, recipientDocument: e.target.value } : prev)} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Total serviços</label>
                      <Input type="number" step="0.01" value={headerEditModal.totalServices} onChange={(e) => setHeaderEditModal((prev) => prev ? { ...prev, totalServices: e.target.value } : prev)} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Desconto</label>
                      <Input type="number" step="0.01" value={headerEditModal.totalDiscount} onChange={(e) => setHeaderEditModal((prev) => prev ? { ...prev, totalDiscount: e.target.value } : prev)} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Valor líquido</label>
                      <Input type="number" step="0.01" value={headerEditModal.netValue} onChange={(e) => setHeaderEditModal((prev) => prev ? { ...prev, netValue: e.target.value } : prev)} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Meio de pagamento</label>
                      <Input type="text" value={headerEditModal.paymentMethod} onChange={(e) => setHeaderEditModal((prev) => prev ? { ...prev, paymentMethod: e.target.value } : prev)} />
                    </div>
                    {hasAdmNotaDownloadKey(headerEditModal.fileNameStorage, headerEditModal.fileName) && (
                      <div className="md:col-span-2">
                        <button
                          type="button"
                          onClick={() => void openAdmNotaSignedDownloadFromHeaderEdit()}
                          disabled={notaDocDownloadBusy}
                          className="inline-flex shrink-0 items-center justify-center gap-1 px-3 py-2 text-sm font-medium text-primary-600 dark:text-primary-400 border border-primary-200 dark:border-primary-800 rounded-lg hover:bg-primary-50 dark:hover:bg-primary-900/20 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <ArrowDownTrayIcon className="h-4 w-4" />
                          Baixar documento
                        </button>
                        {headerEditFileDownloadError && (
                          <p className="text-sm text-red-600 dark:text-red-400 mt-2">{headerEditFileDownloadError}</p>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-gray-200 dark:border-gray-700 pt-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Data pagamento</label>
                  <Input
                    type="date"
                    value={headerEditModal.paymentDate}
                    onChange={(e) => setHeaderEditModal((prev) => (prev ? { ...prev, paymentDate: e.target.value } : prev))}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Observações manuais</label>
                  <textarea
                    rows={3}
                    value={headerEditModal.manualNotes}
                    onChange={(e) => setHeaderEditModal((prev) => (prev ? { ...prev, manualNotes: e.target.value } : prev))}
                    className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={closeHeaderEditModal}
                disabled={submittingHeaderEdit}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => void handleSaveHeaderEdit()}
                disabled={submittingHeaderEdit}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg disabled:opacity-50 transition-colors"
              >
                {submittingHeaderEdit ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {contractEditModal && (
        <AdmDespesasContractEditModal
          modal={contractEditModal}
          submitting={submittingContractEdit}
          onChange={(updater) => setContractEditModal((prev) => (prev ? updater(prev) : prev))}
          onClose={closeContractEditModal}
          onSave={() => void handleSaveContractEdit()}
        />
      )}

      {budgetEditModal && (
        <AdmDespesasBudgetEditModal
          modal={budgetEditModal}
          submitting={submittingBudgetEdit}
          downloadBusy={budgetEditDownloadBusy}
          downloadError={budgetEditDownloadError}
          onChange={(updater) => setBudgetEditModal((prev) => (prev ? updater(prev) : prev))}
          onClose={closeBudgetEditModal}
          onSave={() => void handleSaveBudgetEdit()}
          onDownload={() => void openBudgetEditDownload()}
        />
      )}

      {printDialogView && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
            <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-2">
              Imprimir registros
            </Heading>
            <Text size="sm" color="muted" className="mb-4">
              Selecione o escopo da impressão para a subaba atual.
            </Text>

            <div className="space-y-2">
              <button
                type="button"
                onClick={() => handlePrint('page')}
                className="w-full px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-100 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors text-left"
              >
                Página atual
              </button>
              <button
                type="button"
                onClick={() => handlePrint('filtered')}
                className="w-full px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-100 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors text-left"
              >
                Todos os filtrados
              </button>
            </div>

            <div className="flex justify-end gap-3 mt-4">
              <button
                type="button"
                onClick={() => setPrintDialogView(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de exclusão lógica */}
      {deleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
            <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-2">
              Confirmar Exclusão
            </Heading>
            <Text size="sm" color="muted" className="mb-4">
              Você está excluindo: <strong className="text-gray-900 dark:text-white">{deleteModal.label}</strong>
              <br />
              O registro não será removido do banco de dados (exclusão lógica).
            </Text>

            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Motivo da exclusão <span className="text-red-500">*</span>
            </label>
            <textarea
              rows={3}
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
              placeholder="Informe o motivo..."
              className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
            />

            <div className="flex justify-end gap-3 mt-4">
              <button
                type="button"
                onClick={closeDeleteModal}
                disabled={submittingDelete}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                disabled={submittingDelete || !deleteReason.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-50 transition-colors"
              >
                {submittingDelete ? 'Excluindo...' : 'Confirmar Exclusão'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default AdmDespesas
